import React, { Activity, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { FloatingTree } from '@floating-ui/react';
import { useMediaQuery } from '@uidotdev/usehooks';
import Dexie from 'dexie';
import { useLiveQuery } from 'dexie-react-hooks';
import LocalTimeLibrary from 'local-time';
import { Grid2x2, Hash, MoreHorizontal, Plus, Search, UserRoundPlus, Video, X } from 'lucide-react';
import { UAParser } from 'ua-parser-js';
import { decodeTime } from 'ulid';
import useSound from 'use-sound';
import { Route, Router, useLocation, useParams, useRoute, useSearch } from 'wouter';

import { ACCOUNT_OPTIONS_MODAL_EVENT, openAccountOptionsModal } from '../../lib/accountOptionsModal';
import { apiDelete } from '../../lib/api_v2';
import { BREAKPOINT_SM } from '../../lib/breakpoints';
import { getChannelDisplayName } from '../../lib/channels';
import db, { DEXIE_IS_LOADING } from '../../lib/db';
import { assignDocument } from '../../lib/desktop_navigation_diagnostics';
import { directMessageWorkspaceSummary, storeDirectMessagePayload } from '../../lib/direct_messages_store';
import { loadMessageEntitySnapshots } from '../../lib/entitySnapshots';
import { WORKSPACE_RAIL_LAYOUT_BOUNDS } from '../../lib/layoutSettings';
import { logDebug, logError } from '../../lib/logger';
import { getMessagePreviewAuthorName, getMessagePreviewText, getWidgetAnnouncementPreview } from '../../lib/messagePreview';
import { currentUserId, getMetaContent, rootDomainUrl } from '../../lib/meta';
import { ONBOARDING_INVITE_PEOPLE_EVENT, ONBOARDING_OPEN_WIDGETS_EVENT } from '../../lib/onboardingCardActions';
import { requestLatestJump } from '../../lib/pendingLatestJump';
import { sortULIDs } from '../../lib/stringUtils';
import { useChannelUnreadSignal, useWorkspaceUnreadSignals } from '../../lib/unread_store';
import { useSingleChannelLatestConferenceTranscript } from '../../lib/useSingleChannelLatestConferenceTranscript';
import { useUnreadActivityCount } from '../../lib/useUnreadActivityCount';
import { useUnreadReportChannelIds } from '../../lib/use_unread_report_channel_ids';
import { cn } from '../../lib/utils';
import { SCROLL_TO_WIDGET_EVENT } from '../../lib/widgetEvents';
import { OPEN_WIDGET_BUILDER_EVENT } from '../../lib/widgetGenerationNotifications';
import { markWidgetViewed } from '../../lib/widgetViewedStore';
import {
  pruneDirectMessageIdsByWorkspace,
  pruneWidgetWorkspaces,
  retainWidgetWorkspace,
  stableWidgetWorkspaceRenderOrder,
} from '../../lib/widgetWorkspaceCache';
import { isAvCallingEnabled } from '../../lib/workspace';
import {
  WORKSPACE_RAIL_BOOTSTRAP_READY_EVENT,
  isWorkspaceRailBootstrapReady,
  workspaceRailSourcesReady,
} from '../../lib/workspaceRailBootstrap';
import { getRememberedWorkspaceChannelId, rememberWorkspaceChannelRoute } from '../../lib/workspaceRouteMemory';
import { mainLayoutRelativePath, parseWorkspacePath, workspaceRailPath } from '../../lib/workspaceRoutes';

import DesktopAppPromoImage from '../../../assets/images/desktop-app-promo.png';
import IdleWordmarkIcon from '../../../assets/images/idle-logo.svg';
import RailActivityIcon from '../../../assets/images/rail-activity.svg';
import RailChatIcon from '../../../assets/images/rail-chat.svg';
import StartChatCowImage from '../../../assets/images/start-chat-cow.png';

import { ChatDropzoneContext } from '../../contexts/ChatDropzoneContext';
import { ConferenceProvider, useConference } from '../../contexts/ConferenceContext';
import { EntitySnapshotContext } from '../../contexts/EntitySnapshotContext';
import { KEYCHAIN, KeyMasterProvider, useKeyMaster } from '../../contexts/KeyMaster';
import { RichdownComposerFocusRouterProvider } from '../../contexts/RichdownComposerFocusRouter';
import {
  DirectMessageRouteProvider,
  RouteWorkspaceProvider,
  primeCreatedWorkspaceRoute,
  useRouteWorkspace,
} from '../../contexts/RouteWorkspaceContext';
import { BootSkeleton } from '../../feed/ui/BootSkeleton';
import useComposerDropzone from '../../hooks/useComposerDropzone';
import { useHorizontalResize } from '../../hooks/useHorizontalResize';
import { useLayoutPreferences } from '../../hooks/useLayoutPreferences';
import { useMobileSnapPanels } from '../../hooks/useMobileSnapPanels';
import { useThreadListChannels } from '../../hooks/useThreadListChannels';
import { useWidgetViewedObserver } from '../../hooks/useWidgetViewedObserver';
import { ConferenceBubble, ConferenceCallLayer } from '../ConferenceBubble';
import NagBar from '../NagBar';
import { ResizeHandle } from '../ResizeHandle';
import { ThreadList } from '../ThreadList';
import UnreadManager from '../UnreadManager';
import WorkspacePresenceManager from '../WorkspacePresenceManager';
import DropOverlay from '../messages/DropOverlay';
import AccountOptionsModal from '../modals/AccountOptionsModal';
import AddEmojiModal from '../modals/AddEmojiModal';
import ChannelSettings from '../modals/ChannelSettings';
import DirectMessagePreferencesModal from '../modals/DirectMessagePreferencesModal';
import ErrorReportModal from '../modals/ErrorReportModal';
import InvitePeopleModal from '../modals/InvitePeopleModal';
import MembersModal from '../modals/MembersModal';
import { ModalPortal, useRetainedModalValue } from '../modals/ModalPortal';
import NewChannelModal from '../modals/NewChannelModal';
import NewMessageModal from '../modals/NewMessageModal';
import NewSpaceDescribeModal from '../modals/NewSpaceDescribeModal';
import NewWorkspaceModal from '../modals/NewWorkspaceModal';
import SpaceSetupFlow from '../modals/SpaceSetupFlow';
import WidgetComposerOverlay from '../modals/WidgetComposerOverlay';
import WorkspaceOptionsModal, { WORKSPACE_OPTIONS_MEMBERS_PRESENTATION, WORKSPACE_OPTIONS_SECTION } from '../modals/WorkspaceOptionsModal';
import Flexicon from '../shared/Flexicon';
import LoadingWrapper from '../shared/LoadingWrapper';
import MobileBackButton from '../shared/MobileBackButton';
import SettingsTabs from '../shared/SettingsTabs';
import SurfaceErrorBoundary from '../shared/SurfaceErrorBoundary';
import { Tooltip } from '../shared/Tooltip';
import { WidgetsBoardMenu } from '../shared/WidgetsBoardMenu';
import {
  WORKSPACE_BACKGROUND_IMAGE_CLASS_NAME,
  WorkspaceBackgroundImageLayer,
  preloadWorkspaceBackgroundImage,
  preloadWorkspaceBackgroundImageUrl,
  useWorkspaceBackgroundImage,
} from '../shared/WorkspaceBackgroundImage';
import ActivityFeed from '../sidebar/ActivityFeed';
import { WorkspaceRailContextMenu } from '../sidebar/WorkspaceRailContextMenu';
import HeaderSearch from './HeaderSearch';
import {
  LAYOUT_HEADER_ROW_CLASS_NAME,
  LayoutHeaderNavButton as NavButton,
  LayoutUnreadDot as UnreadDot,
  LayoutWorkspaceHeaderButton as WorkspaceHeaderButton,
} from './LayoutHeaderControls';
import AgentChatPageView from './views/AgentChatPageView';
import BoardView from './views/BoardView';
import ChatView from './views/ChatView';
import WidgetGalleryView from './views/WidgetGalleryView';

export { BREAKPOINT_SM };

const WIDGETS_PANE_VIEW = {
  BOARD: 'board',
  GALLERY: 'gallery',
};
const WIDGET_SHELF_DRAG_INTENT_PX = 8;
const WIDGET_SHELF_DISMISS_FRACTION = 0.33;
// Fixed tile width (w-82 = 328px) + 12px right gutter. The board has no left
// padding, so the pane width is what keeps the right gutter at exactly 12px.
const WIDGETS_PANE_WIDTH = 340;
const WIDGET_SHELF_DISMISS_MAX_PX = 140;
const WIDGET_SHELF_DISMISS_FLICK_VELOCITY = 0.55;
const SCROLL_TO_WIDGET_MAX_ATTEMPTS = 30;
function widgetIdSelectorValue(value) {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(String(value));

  return String(value).replace(/["\\]/g, '\\$&');
}

function getMainLayoutPanelIndex(panel) {
  return panel === 'sidebar' ? 0 : 1;
}

const LayoutNavBarBackdrop = React.memo(function LayoutNavBarBackdrop({ className }) {
  return <div aria-hidden className={cn('header-gradient', className)} />;
});

export const LayoutNavBar = React.memo(function LayoutNavBar({
  children,
  className,
  style,
  borderLeft = false,
  withBackdrop = false,
  backdropClassName,
}) {
  return (
    <div
      className={cn(
        // Floats over the content beneath it so the chat (and other views) scroll up under the bar.
        'absolute inset-x-0 top-0 z-10',
        LAYOUT_HEADER_ROW_CLASS_NAME,
        borderLeft && 'border-l-[0.5px] border-gray-200 dark:border-neutral-800',
        className,
      )}
      style={style}
      data-app-region="drag"
    >
      {withBackdrop ? <LayoutNavBarBackdrop className={backdropClassName} /> : null}
      {children}
    </div>
  );
});

// The Edit half drops out entirely while the board is empty — there is nothing to
// reorder or delete behind the empty-state tile, so the pill collapses to New alone.
// Three separate pills now — New, an overflow menu, and close — rather than a split
// New|Edit pill: the board's arrange actions outgrew a single button, and a menu keeps
// them off the header until asked for.
const WidgetsNewPill = React.memo(function WidgetsNewPill({ onCreate, onImage = false }) {
  return (
    <Tooltip label="New widget" className="flex shrink-0">
      <button
        type="button"
        onClick={onCreate}
        aria-label="New widget"
        data-app-region="none"
        className={cn(
          'flex h-9 shrink-0 cursor-pointer items-center justify-center gap-1.5 rounded-full text-[13px] leading-[22px] font-medium transition-transform duration-150 active:scale-[0.96] max-sm:w-9 sm:pr-3.5 sm:pl-3',
          onImage
            ? 'bg-image-glass-card text-white hover:bg-black/40 dark:hover:bg-black/40'
            : 'border-[0.5px] border-white bg-gray-50 text-black shadow-[0_4px_16px_0_rgba(0,0,0,0.1)] hover:bg-white dark:border-white/10 dark:bg-neutral-900 dark:text-white dark:hover:bg-neutral-800',
        )}
      >
        <Plus className="size-4.5 shrink-0" strokeWidth={2} absoluteStrokeWidth />
        <span className="max-sm:hidden">New</span>
      </button>
    </Tooltip>
  );
});

const WidgetsBoardMenuButton = React.memo(function WidgetsBoardMenuButton({ open, onToggle, onImage = false }) {
  return (
    <Tooltip label="Board options" className={cn('flex shrink-0', open && 'pointer-events-none')}>
      <NavButton onClick={onToggle} active={open} aria-label="Board options" aria-haspopup="menu" aria-expanded={open} onImage={onImage}>
        <MoreHorizontal className={cn('size-4.5', onImage ? 'text-white' : 'text-black')} strokeWidth={2} absoluteStrokeWidth />
      </NavButton>
    </Tooltip>
  );
});

// Replaces all three pills while edit mode is on. `active` inverts the surface so the
// pressed state reads the same as the app's other toggles.
const EditWidgetsButton = React.memo(function EditWidgetsButton({ onClick, active = false, onImage = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      data-app-region="none"
      className={cn(
        'flex h-9 shrink-0 transform-gpu cursor-pointer items-center rounded-full px-3.5 text-[13px] leading-[22px] font-medium transition-transform duration-150 active:scale-[0.96]',
        active
          ? // Same surface recipe as Jump to Present's light mode. Width matches
            // the Create|Add|Edit pill + gap + X it replaces while edit mode is on.
            'w-[292px] justify-center bg-gray-950/80 text-white drop-shadow-lg backdrop-blur hover:bg-gray-950'
          : onImage
            ? 'bg-image-glass-card text-white hover:bg-black/40 dark:hover:bg-black/40'
            : 'border-[0.5px] border-white bg-gray-50 text-black shadow-[0_4px_16px_0_rgba(0,0,0,0.1)] hover:bg-white dark:border-white/10 dark:bg-neutral-900 dark:text-white dark:hover:bg-neutral-800',
      )}
    >
      {active ? 'Done' : 'Edit'}
    </button>
  );
});

// Sits to the right of the nav pill as its own pill — same surface recipe as the
// widgets pane's New pill, with the icon leading the label. On mobile the label drops
// and the pill collapses to a 36px circle matching the other header icon buttons.
const InvitePeopleButton = React.memo(function InvitePeopleButton({ onClick, onImage = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Invite people"
      data-app-region="none"
      className={cn(
        'flex h-9 shrink-0 transform-gpu cursor-pointer items-center justify-center gap-1.5 rounded-full text-[13px] leading-[22px] font-medium transition-transform duration-150 active:scale-[0.96]',
        'max-sm:w-9 sm:pr-3.5 sm:pl-3',
        onImage
          ? 'bg-image-glass-card text-white hover:bg-black/40 dark:hover:bg-black/40'
          : 'border-[0.5px] border-white bg-gray-50 text-black shadow-[0_4px_16px_0_rgba(0,0,0,0.1)] hover:bg-white dark:border-white/10 dark:bg-neutral-900 dark:text-white dark:hover:bg-neutral-800',
      )}
    >
      <UserRoundPlus className="size-4.5 shrink-0" strokeWidth={2} absoluteStrokeWidth />
      <span className="max-sm:hidden">Invite</span>
    </button>
  );
});

export const CloseWidgetsButton = React.memo(function CloseWidgetsButton({ onClick, onImage = false }) {
  return (
    <Tooltip label="Close" className="flex shrink-0">
      <NavButton onClick={onClick} active={false} aria-label="Close" onImage={onImage}>
        <X className={cn('size-4.5', onImage ? 'text-white' : 'text-black')} strokeWidth={2} absoluteStrokeWidth />
      </NavButton>
    </Tooltip>
  );
});

const NATIVE_MACOS_APP_VARIANT = 'native-macos';
// ConferenceBubble is fixed/portaled, so content width alone is not enough to keep it clear of the workspace rail.
const WorkspaceRailWidthContext = React.createContext(WORKSPACE_RAIL_LAYOUT_BOUNDS.initialWidth);

// Dragging under the halfway point between condensed and min snaps the rail into
// its avatar-only condensed form; crossing back snaps it out to minWidth.
const WORKSPACE_RAIL_SNAP = {
  width: WORKSPACE_RAIL_LAYOUT_BOUNDS.condensedWidth,
  threshold: (WORKSPACE_RAIL_LAYOUT_BOUNDS.condensedWidth + WORKSPACE_RAIL_LAYOUT_BOUNDS.minWidth) / 2,
};
const WidgetsPaneControlsContext = React.createContext({
  isChatRoute: false,
  mobileWidgetsActive: false,
  mobileWidgetsOpen: false,
  setMobileWidgetsOpen: () => {},
  toggleWidgets: () => {},
  widgetsPaneOpen: false,
});
const MainLayoutActionsContext = React.createContext({
  openNewMessage: () => {},
});
const WORKSPACE_RAIL_VIEW = {
  ACTIVITY: 'activity',
  WORKSPACES: 'workspaces',
};
const EMPTY_LATEST_MESSAGES_BY_WORKSPACE = {};
const EMPTY_LATEST_MESSAGE_PREVIEWS = {};
const EMPTY_WORKSPACE_RAIL_STATE = {
  workspaceIds: [],
  visibleRailItems: [],
  latestMessagePreviews: EMPTY_LATEST_MESSAGE_PREVIEWS,
  previewsReady: false,
  initialRailSettled: false,
};
const WORKSPACE_OPTIONS_SECTION_QUERY_PARAM = 'workspace-options-section';
const WIDGETS_PANE_QUERY_PARAM = 'widgets';
const WIDGET_QUERY_PARAM = 'widget';

// The New Chat modal and its Create Group step share one modal portal (one backdrop).
const NEW_CHAT_MODAL_VIEW = {
  MESSAGE: 'message',
  GROUP: 'group',
  DESCRIBE: 'describe',
};

// When AI setup is available, "Create a group" opens the describe step instead of the
// manual panel. The manual panel stays mounted as the fallback for when it is not.
const SPACE_SETUP_GENERATION_ENABLED = getMetaContent('workspace:setup_generation_enabled') !== 'false';
const WORKSPACE_OPTIONS_DEEP_LINK_SECTIONS = new Set([
  WORKSPACE_OPTIONS_SECTION.CONNECTIONS,
  WORKSPACE_OPTIONS_SECTION.GROUP_PROFILE,
  WORKSPACE_OPTIONS_SECTION.CHAT_SETTINGS,
  WORKSPACE_OPTIONS_SECTION.CHAT_URL,
  WORKSPACE_OPTIONS_SECTION.NOTIFICATIONS,
  WORKSPACE_OPTIONS_SECTION.MEMBERS,
  WORKSPACE_OPTIONS_SECTION.MEDIA,
]);
const WORKSPACE_RAIL_VIEW_TABS = [
  {
    id: WORKSPACE_RAIL_VIEW.WORKSPACES,
    label: (
      <>
        <RailChatIcon className="size-4" />
        <span className="sr-only">Workspaces</span>
      </>
    ),
    tooltip: 'Chats',
    ariaLabel: 'Show workspaces',
  },
  {
    id: WORKSPACE_RAIL_VIEW.ACTIVITY,
    label: (
      <>
        <RailActivityIcon className="size-4" />
        <span className="sr-only">Activity</span>
      </>
    ),
    tooltip: 'Activity',
    ariaLabel: 'Show activity feed',
  },
];
// ──────────────────────────────────────────────────────────────────────────────
// Workspace cycling
// ──────────────────────────────────────────────────────────────────────────────
// This cycling chord is only mounted in the native macOS app. Web keeps the
// browser's own tab/workspace chords.
//
// CYCLE WORKSPACE (next / prev):
//   Ctrl+Tab / Ctrl+Shift+Tab. No browser chrome intercepts those chords in the
//   native app, so they land in JS. The native wrapper maps Cmd+Shift+[ /
//   Cmd+Shift+] to the same cycle action at the menu level.
// ──────────────────────────────────────────────────────────────────────────────

// One path builder for both anchors and keyboard activation.
// `channelId` is the channel the row is previewing. It outranks the remembered channel so the
// row lands where its "#channel" label says it will; without one this is the old behaviour
// (remembered channel, else the workspace's main channel).
function getWorkspacePickerPath(workspace, { crossDomainLinks = false, useRememberedChannel = true, channelId = null } = {}) {
  const workspaceId = String(workspace.id);
  const rememberedChannelId = useRememberedChannel ? getRememberedWorkspaceChannelId(workspaceId) : null;
  const path = workspaceRailPath(workspace, { channelId: channelId || rememberedChannelId });
  return crossDomainLinks ? rootDomainUrl(path) : path;
}

function getDirectMessagePickerPath(directMessage, { crossDomainLinks = false } = {}) {
  const path = `/messages/${String(directMessage.id)}`;
  return crossDomainLinks ? rootDomainUrl(path) : path;
}

function getAppRouteTargetPath(target) {
  if (!target) return null;

  const value = String(target);
  if (value.includes('://')) return value;
  if (value.startsWith('~')) return value.slice(1) || '/';
  if (value.startsWith('/')) return value;
  return `/${value}`;
}

function railItemKey(item) {
  return `${item.kind}:${item.kind === 'direct_message' ? item.directMessage.id : item.workspace.id}`;
}

function railItemWorkspaceId(item) {
  return String(item.kind === 'direct_message' ? item.directMessage.workspace_id : item.workspace.id);
}

function findRailItemByKey(items, key) {
  return items.find(function matchRailItem(item) {
    return railItemKey(item) === key;
  });
}

function routeSegmentFromPath(path, prefix) {
  const routePrefix = `${prefix}/`;
  if (!path.startsWith(routePrefix)) return null;

  const segment = path.slice(routePrefix.length).split('/')[0];
  if (!segment) return null;

  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

function channelIdFromMainLayoutPath(path) {
  const channelMatch = mainLayoutRelativePath(path)?.match(/^\/chats\/([^/]+)/);
  return channelMatch?.[1] || null;
}

function isChatMainLayoutPath(path) {
  const relativePath = mainLayoutRelativePath(path);
  return Boolean(relativePath && !relativePath.startsWith('/agent') && !relativePath.startsWith('/search'));
}

function isMainLayoutRoute(path) {
  return path === '/chat' || path.startsWith('/messages/') || path.startsWith('/spaces/');
}

function buildWorkspaceRailItems(workspaces = [], directMessages = []) {
  return [
    ...workspaces.map((workspace) => ({ kind: 'workspace', workspace })),
    ...directMessages.map((directMessage) => ({ kind: 'direct_message', directMessage })),
  ];
}

function requestMainLayoutPanel() {
  window.dispatchEvent(
    new CustomEvent('output:activate-panel', {
      detail: { panel: 'main', reason: 'workspace-activated' },
    }),
  );
}

function requestSidebarPanel() {
  window.dispatchEvent(
    new CustomEvent('output:activate-panel', {
      detail: { panel: 'sidebar', reason: 'no-workspace-back' },
    }),
  );
}

// A slower preload from an earlier click must not navigate after a newer selection wins.
let workspacePickerActivationRequest = 0;

function activateMainChannel(mainChannelId) {
  requestLatestJump(mainChannelId);
  window.dispatchEvent(
    new CustomEvent('output:activate-channel', {
      detail: mainChannelId ? { channelId: mainChannelId } : {},
    }),
  );
}

// The active workspace means "jump home"; other workspaces get a Wouter route change.
// `channelId` is the channel the row previews, when it shows one — the row lands there instead
// of home so the click matches the "#channel" it displays.
async function activateWorkspacePickerItem({ workspace, channelId = null, isCurrentWorkspace, navigate }) {
  const activationRequest = ++workspacePickerActivationRequest;
  requestMainLayoutPanel();

  const mainChannelId = workspace.main_channel_id ? String(workspace.main_channel_id) : null;

  if (isCurrentWorkspace) {
    activateMainChannel(channelId || mainChannelId);
    return;
  }

  // Leave the current workspace painted until the destination image is decoded;
  // the route commit can then use it on its first frame instead of flashing the base color.
  await preloadWorkspaceBackgroundImage(workspace);
  if (activationRequest !== workspacePickerActivationRequest) return;

  // Wouter uses `~` to escape this nested `/spaces/:id` router and navigate from app root.
  navigate(`~${getWorkspacePickerPath(workspace, { channelId })}`);
}

async function activateDirectMessagePickerItem({ directMessage, currentDirectMessageId, navigate }) {
  const activationRequest = ++workspacePickerActivationRequest;
  requestMainLayoutPanel();

  const directMessageId = String(directMessage.id);
  if (directMessageId === currentDirectMessageId) {
    const mainChannelId = directMessage.main_channel_id ? String(directMessage.main_channel_id) : null;
    activateMainChannel(mainChannelId);
    return;
  }

  // Direct-message workspaces use the same background payload and must obey the
  // same decode-before-route contract as standard workspaces.
  await preloadWorkspaceBackgroundImage(directMessage);
  if (activationRequest !== workspacePickerActivationRequest) return;
  navigate(`~${getDirectMessagePickerPath(directMessage)}`);
}

function isPlainPrimaryClick(event) {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

// Fresh activation only: no repeat, no IME composition, no already-handled events.
function isFreshActivation(event) {
  return !event.defaultPrevented && !event.repeat && !event.isComposing;
}

// Cycle chord — see platform matrix above.
//   All OSes: Ctrl+Tab = next (+1), Ctrl+Shift+Tab = prev (-1).
// metaKey is rejected so Mac Chrome's native Cmd+Shift+Tab / Cmd+Shift+[ ] / Cmd+Shift+{ }
// bindings are left alone. In Chrome itself the browser consumes Ctrl+Tab before
// JS listeners run; this handler is primarily effective in Electron and in
// browsers that deliver Ctrl+Tab to the page.
function getWorkspaceCycleDirection(event) {
  if (!isFreshActivation(event)) return 0;
  if (event.metaKey || event.altKey) return 0;
  if (!event.ctrlKey) return 0;
  if (event.code !== 'Tab') return 0;
  return event.shiftKey ? -1 : 1;
}

function fetchPreviewAuthor(message) {
  if (message.author_type !== 'profile' || !message.author_id) return null;
  return db.profiles.get([String(message.workspace_id), String(message.author_id)]);
}

// Workspace-list timestamp: clock today, "Yesterday", then a short date for anything older.
const WORKSPACE_PREVIEW_TIME_FORMAT = '%-I:%M %p';
const WORKSPACE_PREVIEW_DATE_FORMAT = '%-m/%-d/%y';

function isSameCalendarDay(left, right) {
  return left.getFullYear() === right.getFullYear() && left.getMonth() === right.getMonth() && left.getDate() === right.getDate();
}

function useCurrentUserRow() {
  const userId = useMemo(() => currentUserId(), []);
  return useLiveQuery(() => (userId ? db.users.get(userId) : null), [userId], null);
}

function useWorkspaceRailBootstrapReady() {
  const [ready, setReady] = useState(() => typeof window !== 'undefined' && isWorkspaceRailBootstrapReady());

  useEffect(() => {
    if (ready) return undefined;

    function handleReady() {
      setReady(true);
    }

    window.addEventListener(WORKSPACE_RAIL_BOOTSTRAP_READY_EVENT, handleReady);
    // Cover the gap between the state initializer and effect registration.
    if (isWorkspaceRailBootstrapReady()) setReady(true);

    return () => {
      window.removeEventListener(WORKSPACE_RAIL_BOOTSTRAP_READY_EVENT, handleReady);
    };
  }, [ready]);

  return ready;
}

function getUserProfileIdsKey(user) {
  return Array.isArray(user?.whoami) ? user.whoami.map(String).sort().join(':') : '';
}

function isCurrentUserMessageAuthor(message, currentUserProfileIds) {
  return message.author_type === 'profile' && !!message.author_id && currentUserProfileIds.has(String(message.author_id));
}

function timestampFromSortOrder(sortOrder) {
  if (!sortOrder) return null;

  try {
    return Math.floor(decodeTime(sortOrder) / 1000);
  } catch {
    return null;
  }
}

// Rail previews join the author and message text. Chat reads "<name>: <message>"; widget
// announcements read as a sentence ("<actor> <verb> <subject>") joined by just a space.
const PREVIEW_AUTHOR_SEPARATOR = ': ';
const ANNOUNCEMENT_PREVIEW_AUTHOR_SEPARATOR = ' ';
// Joins the "#channel" prefix to the rest of the preview: "#Design · Alex: quite impressive ones".
const PREVIEW_CHANNEL_SEPARATOR = ' · ';

async function getWorkspaceMessagePreview(message, currentUserProfileIds) {
  // Widget announcements have no profile author; render "<actor> <verb> <subject>" with the
  // actor bolded and joined to the verb by a bare space so it reads as a sentence, not "<name>: ".
  if (message.type === 'widget_announcement') {
    const { authorName, text } = getWidgetAnnouncementPreview(message);
    if (!text) return null;
    return { authorName, text, authorSeparator: ANNOUNCEMENT_PREVIEW_AUTHOR_SEPARATOR };
  }

  // Resolve entities (so @mentions/#channels render as names instead of "Mention"/"Channel")
  // in parallel with the author lookup — both are independent Dexie reads.
  const [entitySnapshots, author] = await Promise.all([loadMessageEntitySnapshots(message), fetchPreviewAuthor(message)]);

  const text = getMessagePreviewText(message, entitySnapshots);
  if (!text) return null;

  return {
    authorName: isCurrentUserMessageAuthor(message, currentUserProfileIds) ? 'you' : getMessagePreviewAuthorName(author),
    text,
  };
}

// Rail previews prefix "#channel" only for workspaces that actually have more than one
// channel. `useThreadListChannels` draws that same line — a workspace's extra channels are
// its open_breakouts — so the prefix shows exactly when the channel switcher does, and
// single-channel workspaces (and DMs) keep the plain "<name>: <message>" preview. Messages
// from the workspace's own main channel go unprefixed too — that channel *is* the workspace.
//
// Reading through [workspace_id+kind] rather than counting rows also skips the
// `pseudotype: 'skeleton'` placeholders the journal writes into db.channels: those carry no
// kind, so a plain count would report extra channels for a workspace that has none.
async function loadRailChannelContext(workspaceIds) {
  const [channels, workspaces] = await Promise.all([
    db.channels
      .where('[workspace_id+kind]')
      .anyOf(
        workspaceIds.flatMap(function channelKindKeys(workspaceId) {
          return [
            [workspaceId, 'public'],
            [workspaceId, 'open_breakout'],
          ];
        }),
      )
      .toArray(),
    db.workspaces.bulkGet(workspaceIds),
  ]);

  const mainChannelKeys = new Set(
    workspaces
      .filter((workspace) => workspace?.main_channel_id)
      .map((workspace) => `${String(workspace.id)}:${String(workspace.main_channel_id)}`),
  );

  const multiChannelWorkspaceIds = new Set();
  const channelNames = new Map();

  channels.forEach((channel) => {
    const workspaceId = String(channel.workspace_id);
    const channelKey = `${workspaceId}:${channel.id}`;
    // The main channel is the workspace itself, not a place inside it — its messages read as
    // a plain "<name>: <message>", matching the thread list's unprefixed home row.
    if (!mainChannelKeys.has(channelKey)) channelNames.set(channelKey, getChannelDisplayName(channel));
    if (channel.kind === 'open_breakout') multiChannelWorkspaceIds.add(workspaceId);
  });

  return { multiChannelWorkspaceIds, channelNames };
}

const EMPTY_RAIL_PREVIEW_CHANNEL = { channelId: null, channelName: null };

function railPreviewChannel(workspaceId, message, { multiChannelWorkspaceIds, channelNames }) {
  if (!multiChannelWorkspaceIds.has(workspaceId) || !message.channel_id) return EMPTY_RAIL_PREVIEW_CHANNEL;

  const channelId = String(message.channel_id);
  // A channel that has not replicated yet drops the prefix rather than rendering
  // getChannelDisplayName's "Untitled" fallback into the rail.
  const channelName = channelNames.get(`${workspaceId}:${channelId}`);
  return channelName ? { channelId, channelName } : EMPTY_RAIL_PREVIEW_CHANNEL;
}

function sortRailItemsByLatestMessage(items, latestMessagesByWorkspace) {
  return [...items].sort((left, right) => {
    const leftActivity = railItemActivity(left, latestMessagesByWorkspace);
    const rightActivity = railItemActivity(right, latestMessagesByWorkspace);

    if (leftActivity.sortOrder && rightActivity.sortOrder) return sortULIDs(rightActivity.sortOrder, leftActivity.sortOrder);
    if (leftActivity.timestamp != null && rightActivity.timestamp != null) return rightActivity.timestamp - leftActivity.timestamp;
    if (leftActivity.sortOrder || leftActivity.timestamp != null) return -1;
    if (rightActivity.sortOrder || rightActivity.timestamp != null) return 1;
    return 0;
  });
}

function railItemActivity(item, latestMessagesByWorkspace) {
  const workspaceId = railItemWorkspaceId(item);
  const latestMessage = latestMessagesByWorkspace?.[workspaceId];

  if (latestMessage?.sort_order) {
    return {
      sortOrder: latestMessage.sort_order,
      timestamp: latestMessage.timestamp ?? timestampFromSortOrder(latestMessage.sort_order),
    };
  }

  if (item.kind === 'direct_message') {
    const sortOrder = item.directMessage.last_message_sort_order;
    if (sortOrder) {
      return {
        sortOrder,
        timestamp: timestampFromSortOrder(sortOrder),
      };
    }
    // A brand-new DM has no messages yet; sort it by creation like a fresh workspace
    // (below), or it would have no activity at all and sink to the bottom of the rail.
    return {
      sortOrder: null,
      timestamp: item.directMessage.created_at ?? null,
    };
  }

  return {
    sortOrder: null,
    timestamp: item.workspace.created_at,
  };
}

function orderRailItemsByPreviousKeys(items, previousKeys) {
  if (!previousKeys.length) return items;

  const itemByKey = new Map(items.map((item) => [railItemKey(item), item]));
  const usedKeys = new Set();
  const orderedItems = [];

  previousKeys.forEach((key) => {
    const item = itemByKey.get(key);
    if (!item) return;

    orderedItems.push(item);
    usedKeys.add(key);
  });

  items.forEach((item) => {
    const key = railItemKey(item);
    if (!usedKeys.has(key)) orderedItems.push(item);
  });

  return orderedItems;
}

// Keep the order query limited to the bounded rail projection. Preview parsing joins
// profiles/entities and parses richdown, but none of that is needed to settle row positions.
function useWorkspaceLatestMessageActivityState(workspaceIds, enabled) {
  const workspaceIdsKey = useMemo(() => [...workspaceIds].sort().join(':'), [workspaceIds]);
  const result = useLiveQuery(async () => {
    if (!enabled) {
      return { enabled: false, workspaceIdsKey, latestMessagesByWorkspace: {} };
    }

    const entries = await Promise.all(
      workspaceIds.map(async function latestMessageForWorkspace(workspaceId) {
        const latestMessage = await db.workspace_latest_messages
          .where('[workspace_id+sort_order]')
          .between([workspaceId, Dexie.minKey], [workspaceId, Dexie.maxKey], false, true)
          .reverse()
          .first();
        return latestMessage ? [workspaceId, latestMessage] : null;
      }),
    );

    return {
      enabled: true,
      workspaceIdsKey,
      latestMessagesByWorkspace: Object.fromEntries(entries.filter(Boolean)),
    };
  }, [enabled, workspaceIdsKey]);

  const activityReady = enabled && result?.enabled === true && result.workspaceIdsKey === workspaceIdsKey;

  return {
    workspaceIdsKey,
    latestMessagesByWorkspace: result?.latestMessagesByWorkspace || EMPTY_LATEST_MESSAGES_BY_WORKSPACE,
    activityReady,
  };
}

function useWorkspaceLatestMessagePreviewState(workspaceIdsKey, latestMessagesByWorkspace, enabled) {
  const user = useCurrentUserRow();
  const currentUserProfileIdsKey = getUserProfileIdsKey(user);
  const currentUserProfileIds = useMemo(
    () => new Set(currentUserProfileIdsKey ? currentUserProfileIdsKey.split(':') : []),
    [currentUserProfileIdsKey],
  );
  // Reuse prior per-workspace preview objects when content is unchanged so
  // React.memo on WorkspacePickerItem can skip re-rendering siblings on every Dexie tick.
  const previousPreviewsRef = useRef({});

  const result = useLiveQuery(async () => {
    if (!enabled) {
      return { enabled: false, workspaceIdsKey, previews: {} };
    }

    const latestMessageEntries = Object.entries(latestMessagesByWorkspace);
    if (!latestMessageEntries.length) {
      previousPreviewsRef.current = {};
      return { enabled: true, workspaceIdsKey, previews: {} };
    }

    const previousPreviews = previousPreviewsRef.current;
    const railChannelContext = await loadRailChannelContext(latestMessageEntries.map(([workspaceId]) => workspaceId));
    const entries = await Promise.all(
      latestMessageEntries.map(async function latestMessagePreviewForWorkspace([workspaceId, latestMessage]) {
        const preview = await getWorkspaceMessagePreview(latestMessage, currentUserProfileIds);
        const { channelId, channelName } = railPreviewChannel(workspaceId, latestMessage, railChannelContext);
        const nextPreview = {
          sortOrder: latestMessage.sort_order,
          authorName: preview?.authorName || null,
          authorSeparator: preview?.authorSeparator || PREVIEW_AUTHOR_SEPARATOR,
          channelId,
          channelName,
          text: preview?.text || null,
          timestamp: latestMessage.timestamp ?? null,
        };

        const previous = previousPreviews[workspaceId];
        const stable =
          previous &&
          previous.sortOrder === nextPreview.sortOrder &&
          previous.text === nextPreview.text &&
          previous.authorName === nextPreview.authorName &&
          previous.authorSeparator === nextPreview.authorSeparator &&
          previous.channelId === nextPreview.channelId &&
          previous.channelName === nextPreview.channelName &&
          previous.timestamp === nextPreview.timestamp
            ? previous
            : nextPreview;
        return [workspaceId, stable];
      }),
    );

    const next = Object.fromEntries(entries.filter(Boolean));
    previousPreviewsRef.current = next;
    return { enabled: true, workspaceIdsKey, previews: next };
  }, [currentUserProfileIds, enabled, latestMessagesByWorkspace, workspaceIdsKey]);

  const previewsReady = enabled && result?.enabled === true && result.workspaceIdsKey === workspaceIdsKey;
  const latestMessagePreviews = result?.previews || EMPTY_LATEST_MESSAGE_PREVIEWS;

  return {
    latestMessagePreviews,
    previewsReady,
  };
}

function useStableVisibleRailItems(railItems, latestMessagesByWorkspace, activityReady) {
  const previousKeysRef = useRef([]);
  const visibleRailItems = useMemo(
    () =>
      activityReady
        ? sortRailItemsByLatestMessage(railItems, latestMessagesByWorkspace)
        : orderRailItemsByPreviousKeys(railItems, previousKeysRef.current),
    [activityReady, latestMessagesByWorkspace, railItems],
  );

  useEffect(() => {
    if (activityReady) previousKeysRef.current = visibleRailItems.map(railItemKey);
  }, [activityReady, visibleRailItems]);

  return visibleRailItems;
}

export function useWorkspaceRailState(
  workspaces = [],
  directMessages = [],
  { railSourcesReady = true } = {},
) {
  const railItems = useMemo(() => buildWorkspaceRailItems(workspaces, directMessages), [directMessages, workspaces]);
  // DMs and workspaces can point at the same workspace; dedupe so preview/unread
  // subscriptions do one Dexie read per backing workspace while railItems keeps every row.
  const workspaceIds = useMemo(() => [...new Set(railItems.map(railItemWorkspaceId))], [railItems]);
  const activityEnabled = railSourcesReady;
  const { workspaceIdsKey, latestMessagesByWorkspace, activityReady } = useWorkspaceLatestMessageActivityState(
    workspaceIds,
    activityEnabled,
  );
  const { latestMessagePreviews, previewsReady } = useWorkspaceLatestMessagePreviewState(
    workspaceIdsKey,
    latestMessagesByWorkspace,
    activityReady,
  );
  const visibleRailItems = useStableVisibleRailItems(railItems, latestMessagesByWorkspace, activityReady);
  // Names and avatars come from the workspace/DM identity snapshots. Do not hold
  // them behind the independent latest-message query: cached rows can render now,
  // while preview text and activity ordering settle in place when their projection arrives.

  return useMemo(
    () => ({
      workspaceIds,
      visibleRailItems,
      latestMessagePreviews,
      previewsReady,
      initialRailSettled: railSourcesReady,
    }),
    [latestMessagePreviews, previewsReady, railSourcesReady, visibleRailItems, workspaceIds],
  );
}

function loadRailWorkspaces() {
  return db.workspaces.orderBy('slug').toArray();
}

function loadRailDirectMessages() {
  return db.direct_messages
    .orderBy('updated_at')
    .reverse()
    .filter((directMessage) => directMessage.visible !== false)
    .toArray();
}

export function useAuthenticatedShellRailState() {
  const initialRailBootstrapReady = useWorkspaceRailBootstrapReady();
  // Dexie automatically re-runs these queries when their tables change. Keeping the
  // separate bootstrap-ready signal out of the dependency arrays is important: adding
  // it resets a warm query to DEXIE_IS_LOADING just as the network snapshot completes,
  // briefly removing every cached rail row before returning the same data.
  const workspaces = useLiveQuery(loadRailWorkspaces, [], DEXIE_IS_LOADING);
  const directMessages = useLiveQuery(loadRailDirectMessages, [], DEXIE_IS_LOADING);
  const workspacesReady = workspaces !== DEXIE_IS_LOADING;
  const directMessagesReady = directMessages !== DEXIE_IS_LOADING;
  const resolvedWorkspaces = workspacesReady ? workspaces || [] : [];
  const resolvedDirectMessages = directMessagesReady ? directMessages || [] : [];
  // Warm Dexie caches should paint immediately. On a true cold start, keep the
  // skeleton until the two identity snapshots finish so an empty rail does not flash.
  const railSourcesReady = workspaceRailSourcesReady({
    bootstrapReady: initialRailBootstrapReady,
    workspacesReady,
    directMessagesReady,
    cachedRowCount: resolvedWorkspaces.length + resolvedDirectMessages.length,
  });

  return useWorkspaceRailState(resolvedWorkspaces, resolvedDirectMessages, { railSourcesReady });
}

// Lives under KeyMasterProvider so modals/popovers can suppress workspace cycling.
function WorkspaceCycleRouter({ items, currentItemKey, onActivateItem }) {
  const { unlocked } = useKeyMaster();

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!unlocked(KEYCHAIN.RICHDOWN_COMPOSER__FOCUS_ROUTER)) return;
      if (!items?.length) return;

      const direction = getWorkspaceCycleDirection(event);
      if (direction === 0) return;

      const currentIndex = items.findIndex(function matchCurrent(item) {
        return railItemKey(item) === currentItemKey;
      });
      // Unknown current workspace falls back to the first entry for "next" and the last for "prev".
      const startIndex = currentIndex === -1 ? (direction === 1 ? -1 : 0) : currentIndex;
      const nextIndex = (startIndex + direction + items.length) % items.length;
      const nextItem = items[nextIndex];
      if (!nextItem) return;

      event.preventDefault();
      onActivateItem(nextItem);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentItemKey, items, onActivateItem, unlocked]);

  return null;
}

// Lives under KeyMasterProvider so modals/popovers can suppress the shortcut.
// Cmd+, mirrors the macOS "Settings/Preferences" convention and opens the current
// space (or DM) settings. Desktop-only by design: in a browser the OS/browser claims
// Cmd+, for its own settings, and intercepting it from the page is unreliable.
function WorkspaceSettingsShortcutRouter({ onOpenSettings }) {
  const { unlocked } = useKeyMaster();

  useEffect(() => {
    function handleKeyDown(event) {
      if (!isFreshActivation(event)) return;
      // Plain Cmd+, only — reject Ctrl/Alt/Shift so Cmd+Shift+, ("<") and friends pass through.
      if (!event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
      if (event.code !== 'Comma') return;
      // Only at base UI — a modal/popover on top of the KeyMaster stack suppresses it.
      if (!unlocked(KEYCHAIN.RICHDOWN_COMPOSER__FOCUS_ROUTER)) return;

      event.preventDefault();
      onOpenSettings();
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onOpenSettings, unlocked]);

  // Newer desktop builds bind Cmd+, to a native "Settings…" menu item. That accelerator
  // swallows the keystroke before the page sees it and signals us over IPC instead, so the
  // keydown handler above is the fallback for builds without the menu item. Older builds
  // without this bridge return undefined here, which is a valid no-op cleanup.
  useEffect(() => {
    return window.electron?.onOpenWorkspaceSettings?.(onOpenSettings);
  }, [onOpenSettings]);

  return null;
}

// Lives under KeyMasterProvider so modals/popovers can suppress the shortcut.
// Cmd+F is desktop-only because browser builds should keep the browser's native find.
// Cmd+F toggles search (mirrors the nav icon): it opens search from chat and exits back
// out when already on /search. Both delivery paths call the same toggle handler — see
// the IPC note below — so the toggle behaves identically whether or not the build has a
// native menu item, matching how Cmd+, (WorkspaceSettingsShortcutRouter) is wired.
function WorkspaceSearchShortcutRouter({ onToggleSearch }) {
  const { unlocked } = useKeyMaster();

  useEffect(() => {
    function handleKeyDown(event) {
      if (!isFreshActivation(event)) return;
      if (!event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
      if (event.code !== 'KeyF') return;
      if (!unlocked(KEYCHAIN.RICHDOWN_COMPOSER__FOCUS_ROUTER)) return;

      event.preventDefault();
      onToggleSearch();
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onToggleSearch, unlocked]);

  // Newer desktop builds bind Cmd+F to a native menu item. That accelerator swallows the
  // keystroke before the page sees it and signals us over IPC instead, so the keydown
  // handler above is the fallback for builds without the menu item. Both paths route to
  // onToggleSearch so Cmd+F toggles in and out of search in the desktop app too. Older
  // builds without this bridge return undefined here, which is a valid no-op cleanup.
  useEffect(() => {
    return window.electron?.onOpenWorkspaceSearch?.(onToggleSearch);
  }, [onToggleSearch]);

  return null;
}

// Escape exits search, mirroring the nav icon's toggle-out. Unlike Cmd+F this works in
// every build — Escape collides with no browser/OS chord — so it isn't gated on the
// native-app variant. Only listens while on /search, and defers to any open
// modal/popover (which sits above the composer's base layer in KeyMaster).
function SearchEscapeRouter({ active, onCloseSearch }) {
  const { unlocked } = useKeyMaster();

  useEffect(() => {
    if (!active) return undefined;

    function handleKeyDown(event) {
      if (!isFreshActivation(event)) return;
      if (event.key !== 'Escape') return;
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
      if (!unlocked(KEYCHAIN.RICHDOWN_COMPOSER__FOCUS_ROUTER)) return;

      event.preventDefault();
      onCloseSearch();
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [active, onCloseSearch, unlocked]);

  return null;
}

const WORKSPACE_RAIL_IMAGE_TEXT_SHADOW_CLASS = '[filter:drop-shadow(0_2px_25px_#000)]';
// Dividers vanish next to the hovered row (sibling selectors: the divider after a hovered row, and
// the divider that :has a hovered row right after it) so the rounded hover fill gets clean edges.
const WORKSPACE_RAIL_DIVIDER_CLASS =
  // transform-gpu + will-change promote each divider onto its own compositor layer: without
  // them, Safari skips repainting these 1px strips inside the scrolled rail, so opacity
  // changes (hover/selection fades) silently never render at short window heights.
  'h-px w-[calc(100%-36px)] shrink-0 bg-gray-300/60 transition-opacity duration-300 ease-fluid transform-gpu will-change-[opacity] dark:bg-white/10 [:hover+&]:opacity-0 [&:has(+:hover)]:opacity-0';
const WORKSPACE_PREVIEW_TIMESTAMP_CLASS = 'shrink-0 text-[11px]/4 whitespace-nowrap lining-nums proportional-nums transition-none';

// Format the rail timestamp in render with local-time's pure strftime helper rather than rendering a
// <LocalTime> element. <LocalTime> paints date.toLocaleString() (full date + time) and depends on the
// library's run() to rewrite it in place — but the library stamps processed nodes with [data-localized]
// and never re-localizes them. Rail rows are reused across messages, so an in-place timestamp update on
// an already-localized node was skipped by run() and stayed stuck on the raw toLocaleString() fallback
// until the row remounted. Computing the string here keeps it correct on every render.
//   Today    → "3:45 PM"
//   Yesterday → "Yesterday"
//   Older    → "6/8/26"
function formatWorkspacePreviewTimestamp(messageDate, now) {
  if (isSameCalendarDay(messageDate, now)) {
    return LocalTimeLibrary.strftime(messageDate, WORKSPACE_PREVIEW_TIME_FORMAT);
  }

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (isSameCalendarDay(messageDate, yesterday)) {
    return 'Yesterday';
  }

  return LocalTimeLibrary.strftime(messageDate, WORKSPACE_PREVIEW_DATE_FORMAT);
}

const WorkspacePreviewTimestamp = React.memo(function WorkspacePreviewTimestamp({ timestamp, emphasized, onImage = false }) {
  const className = cn(
    WORKSPACE_PREVIEW_TIMESTAMP_CLASS,
    onImage
      ? emphasized
        ? cn('font-medium text-white', WORKSPACE_RAIL_IMAGE_TEXT_SHADOW_CLASS)
        : cn('font-normal text-white', WORKSPACE_RAIL_IMAGE_TEXT_SHADOW_CLASS)
      : emphasized
        ? 'font-medium text-black dark:text-neutral-100'
        : 'font-normal text-gray-500 dark:text-neutral-500',
  );
  const messageDate = new Date(timestamp * 1000);
  const label = formatWorkspacePreviewTimestamp(messageDate, new Date());

  return (
    <time dateTime={messageDate.toISOString()} className={className}>
      {label}
    </time>
  );
});

const WorkspacePickerSkeletonItem = React.memo(function WorkspacePickerSkeletonItem({ item }) {
  const avatarRadius = item.kind === 'direct_message' ? 'rounded-full' : 'rounded';

  return (
    <div aria-hidden className="relative w-full shrink-0">
      <div className="flex w-full min-w-0 items-center gap-3 rounded-[12px] p-[14px]">
        <span className={cn('size-12 shrink-0 animate-pulse bg-gray-200 dark:bg-neutral-700', avatarRadius)} />
        <span className="flex min-w-0 flex-1 flex-col gap-2">
          <span className="h-4 w-2/3 animate-pulse rounded-full bg-gray-200 dark:bg-neutral-700" />
          <span className="h-3 w-4/5 animate-pulse rounded-full bg-gray-100 dark:bg-neutral-800" />
        </span>
      </div>
    </div>
  );
});

const WorkspacePickerItem = React.memo(function WorkspacePickerItem({
  item,
  isActive,
  onActivateItem,
  onOpenContextMenu,
  unreadSignal,
  latestMessagePreview,
  previewReady,
  crossDomainLinks = false,
  condensed = false,
}) {
  const isDirectMessage = item.kind === 'direct_message';
  const workspace = item.workspace || null;
  const directMessage = item.directMessage || null;
  const directMessageWorkspaceId = isDirectMessage ? String(directMessage.workspace_id) : null;
  const directMessagePeerProfileId = isDirectMessage ? String(directMessage.peer_profile.id) : null;
  // Keep the DM display identity on the row's profile subscription so avatar/name
  // changes update only the affected rail item instead of rewriting DM snapshots.
  const directMessagePeerProfile = useLiveQuery(
    () =>
      directMessageWorkspaceId && directMessagePeerProfileId
        ? db.profiles.get([directMessageWorkspaceId, directMessagePeerProfileId])
        : null,
    [directMessagePeerProfileId, directMessageWorkspaceId],
    isDirectMessage ? directMessage.peer_profile : null,
  );
  const directMessageDisplayResource = isDirectMessage ? directMessagePeerProfile || directMessage.peer_profile : null;
  // Live-call badge: calls always run in the chat's main channel, so the row watches
  // that channel's latest transcript the same way AvNavButton does.
  const mainChannelId = isDirectMessage
    ? directMessage.main_channel_id
      ? String(directMessage.main_channel_id)
      : null
    : workspace.main_channel_id
      ? String(workspace.main_channel_id)
      : null;
  const latestConferenceTranscript = useSingleChannelLatestConferenceTranscript(mainChannelId);
  const hasLiveConference =
    latestConferenceTranscript !== DEXIE_IS_LOADING &&
    !!latestConferenceTranscript?.data?.started_at &&
    !latestConferenceTranscript?.data?.ended_at;
  const title = isDirectMessage ? directMessageDisplayResource.full_name : workspace.title;
  const flexiconResource = isDirectMessage ? directMessageDisplayResource : workspace;
  // Prefer the medium variant for these small rows; fall back to the full-size url (large/original).
  const backgroundSource = isDirectMessage ? directMessage : workspace;
  const {
    backgroundImageUrl,
    hasWorkspaceBackgroundImage: hasBackgroundImage,
    workspaceBackgroundImageStyle: backgroundImageStyle,
  } = useWorkspaceBackgroundImage(backgroundSource);
  const {
    text: previewText = null,
    authorName: previewAuthorName = null,
    authorSeparator: previewAuthorSeparator = PREVIEW_AUTHOR_SEPARATOR,
    channelId: previewChannelId = null,
    channelName: previewChannelName = null,
    timestamp: previewTimestamp = null,
  } = latestMessagePreview ?? {};
  // DMs are single-channel, so the preview builder never hands them a channel; the guard
  // keeps the prefix off DM rows regardless.
  const hasPreview = Boolean(previewReady && previewText);
  const showPreviewChannel = !isDirectMessage && Boolean(previewChannelName);
  const previewChannelLabel = showPreviewChannel ? `#${previewChannelName}` : '';
  // Whenever the row displays a channel, the row goes to that channel — the label and the
  // click target are always the same place.
  const rowChannelId = showPreviewChannel ? previewChannelId : null;
  const itemPath = isDirectMessage
    ? getDirectMessagePickerPath(directMessage, { crossDomainLinks })
    : getWorkspacePickerPath(workspace, { crossDomainLinks, useRememberedChannel: !isActive, channelId: rowChannelId });
  const previewBody =
    previewAuthorName && previewText
      ? `${previewAuthorName}${previewAuthorSeparator}${previewText}`
      : previewText || previewAuthorName || '';
  const previewLabel = [previewChannelLabel, previewBody].filter(Boolean).join(PREVIEW_CHANNEL_SEPARATOR);
  const mentionCount = unreadSignal?.mentions || 0;
  const unreadCount = unreadSignal?.messages || 0;
  const hasMentionUnread = mentionCount > 0;
  const hasUnread = hasMentionUnread || unreadCount > 0;
  const mentionLabel = mentionCount === 1 ? '1 mention' : `${mentionCount} mentions`;
  const unreadSuffix = hasMentionUnread ? mentionLabel : hasUnread ? 'unread' : null;
  const baseAriaLabel = isActive ? `Go to ${title}` : isDirectMessage ? `Open direct message with ${title}` : `Open workspace ${title}`;
  const ariaLabel = [
    baseAriaLabel,
    hasLiveConference ? 'call in progress' : null,
    unreadSuffix,
    previewLabel ? `latest message: ${previewLabel}` : null,
  ]
    .filter(Boolean)
    .join(', ');
  const titleLabel = [title, unreadSuffix].filter(Boolean).join(' ');
  // Rows sit flat on the rail until selected: the selected row gets a white container,
  // or its background image fill when the chat has one.
  const showsBackgroundImage = hasBackgroundImage && isActive;
  const handleClick = useCallback(
    function handleWorkspacePickerClick(event) {
      if (!isPlainPrimaryClick(event)) return;
      if (crossDomainLinks) return;

      event.preventDefault();
      onActivateItem(item, rowChannelId);
    },
    [crossDomainLinks, item, onActivateItem, rowChannelId],
  );

  const handleContextMenu = useCallback(
    function handleWorkspacePickerContextMenu(event) {
      if (!onOpenContextMenu || crossDomainLinks) return;

      event.preventDefault();
      event.stopPropagation();
      onOpenContextMenu(event, item);
    },
    [crossDomainLinks, item, onOpenContextMenu],
  );
  const handlePreloadBackgroundImage = useCallback(
    function preloadBackgroundImage() {
      preloadWorkspaceBackgroundImageUrl(backgroundImageUrl);
    },
    [backgroundImageUrl],
  );

  const rowLink = (
    <a
      href={itemPath}
      draggable={false}
      aria-current={isActive ? 'page' : undefined}
      aria-label={ariaLabel}
      // The floating tooltip replaces the native title in condensed mode so the two never show together.
      title={condensed ? undefined : titleLabel}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      onFocus={handlePreloadBackgroundImage}
      onPointerEnter={handlePreloadBackgroundImage}
      className={cn(
        'group relative flex w-full min-w-0 cursor-pointer items-center gap-3 overflow-hidden rounded-[12px] p-[14px] text-black outline-none select-none',
        showsBackgroundImage
          ? 'bg-white text-white'
          : isActive
            ? 'border-[0.5px] border-white bg-gray-50 p-[13.5px]'
            : 'bg-transparent hover:bg-gray-100/50 dark:hover:bg-white/5',
      )}
    >
      {showsBackgroundImage ? (
        <>
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 scale-120 bg-cover bg-center bg-no-repeat blur-[18px]"
            style={backgroundImageStyle}
          />
          <span aria-hidden className="pointer-events-none absolute inset-0 bg-black mix-blend-soft-light" />
        </>
      ) : null}
      <Flexicon
        resource={flexiconResource}
        size="size-12"
        rounded={isDirectMessage ? 'rounded-full' : 'rounded'}
        className={cn(
          'relative z-20 size-12 shrink-0 transform-gpu border-[0.5px] transition-transform duration-300 ease-fluid will-change-transform',
          // Expanded rows slide the flexicon left on hover; condensed rows scale it down instead.
          condensed ? 'group-hover:scale-95' : 'group-hover:-translate-x-1',
          showsBackgroundImage ? 'border-white/15' : 'border-black/10',
        )}
      />
      {/* Condensed rail: the text-area badges are clipped away, so the count rides the
            flexicon's top-right corner (no @ icon here — just the number). Anchored right
            so wider counts grow leftward over the avatar instead of into the clip edge. */}
      {condensed && hasUnread ? (
        <span
          aria-hidden
          className={cn(
            'absolute top-[10px] right-[10px] z-30 inline-flex min-w-[17px] items-center justify-center rounded-full px-[5px] pt-[2px] pb-[3px] text-[11px]/3 font-bold tabular-nums',
            showsBackgroundImage ? 'bg-white text-black mix-blend-screen' : 'bg-idle-blue text-white',
          )}
        >
          {(unreadCount || mentionCount) > 99 ? '99+' : unreadCount || mentionCount}
        </span>
      ) : null}
      {/* relative without z-index: a z-index here would create a stacking context and break the
            unread pill's mix-blend-screen punch-through against the background image */}
      <span className="relative flex min-w-0 flex-1 flex-col gap-px pt-[4px] pb-[6px]">
        <span className="flex w-full items-center gap-2">
          <span className="flex min-w-0 flex-1 items-center gap-2">
            <span className={cn('min-w-0 flex-1', showsBackgroundImage && WORKSPACE_RAIL_IMAGE_TEXT_SHADOW_CLASS)}>
              <span
                className={cn(
                  'block truncate text-[15px]/5 font-semibold transition-none',
                  showsBackgroundImage ? 'text-white' : 'text-black dark:text-neutral-100',
                )}
              >
                {title}
              </span>
            </span>
            {hasMentionUnread || unreadCount > 0 ? (
              <span className="flex shrink-0 items-center gap-0.5" aria-hidden>
                {hasMentionUnread ? (
                  <span
                    className={cn(
                      'inline-flex size-[17px] items-center justify-center text-cursed leading-none font-bold transition-none',
                      showsBackgroundImage ? cn('text-white', WORKSPACE_RAIL_IMAGE_TEXT_SHADOW_CLASS) : 'text-idle-blue',
                    )}
                  >
                    @
                  </span>
                ) : null}
                {unreadCount > 0 ? (
                  <span
                    className={cn(
                      'inline-flex min-w-[17px] items-center justify-center rounded-full px-[5px] pt-[2px] pb-[3px] text-[11px]/3 font-bold tabular-nums',
                      showsBackgroundImage ? 'bg-white text-black mix-blend-screen' : 'bg-idle-blue text-white',
                    )}
                  >
                    <span>{unreadCount > 99 ? '99+' : unreadCount}</span>
                  </span>
                ) : null}
              </span>
            ) : null}
          </span>
          {previewReady && previewTimestamp ? (
            <WorkspacePreviewTimestamp timestamp={previewTimestamp} emphasized={hasUnread} onImage={showsBackgroundImage} />
          ) : null}
        </span>
        <span
          aria-hidden={!hasPreview}
          className={cn('max-w-[85%] min-w-0', showsBackgroundImage && WORKSPACE_RAIL_IMAGE_TEXT_SHADOW_CLASS)}
        >
          <span
            className={cn(
              'block truncate text-[13px]/4 transition-none',
              showsBackgroundImage ? 'text-white' : 'text-gray-500 dark:text-neutral-500',
            )}
          >
            {hasPreview && showPreviewChannel ? (
              <span className="font-semibold">{`${previewChannelLabel}${PREVIEW_CHANNEL_SEPARATOR}`}</span>
            ) : null}
            {/* DM previews skip the author prefix — it's always either you or the person the row is named after. */}
            {hasPreview && previewAuthorName && !isDirectMessage ? (
              <span className="font-semibold">{`${previewAuthorName}${previewAuthorSeparator}`}</span>
            ) : null}
            <span className="font-normal">{hasPreview ? previewText : '\u00a0'}</span>
          </span>
        </span>
      </span>
    </a>
  );

  return (
    <div className="relative w-full shrink-0">
      {condensed ? (
        <Tooltip label={title} placement="right" followCursor={false} className="flex w-full">
          {rowLink}
        </Tooltip>
      ) : (
        rowLink
      )}
    </div>
  );
});

const WorkspaceRailProfileButton = React.memo(function WorkspaceRailProfileButton({ userEmail, user }) {
  const accountLabel = userEmail || user?.full_name || 'Profile';
  const accountInitial = accountLabel.trim().charAt(0).toUpperCase();
  const tooltipLabel = user?.full_name || userEmail || 'Profile';

  return (
    <Tooltip label={tooltipLabel} className="flex shrink-0">
      <button
        type="button"
        onClick={openAccountOptionsModal}
        data-app-region="none"
        aria-label={`Account: ${accountLabel}`}
        className="flex size-6 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-full text-[10px] leading-none font-semibold text-gray-700 outline-none dark:text-neutral-200"
      >
        {user?.flexicon ? (
          <Flexicon resource={user} size="size-6" rounded="rounded-full" className="size-6" />
        ) : (
          <span className="flex size-6 items-center justify-center rounded-full bg-white dark:bg-neutral-700">{accountInitial}</span>
        )}
      </button>
    </Tooltip>
  );
});

// Web-only pitch for the desktop app, floating over the bottom of the rail's scroll area — rows scroll
// underneath and clip against the card itself, not a straight line above it. Native shells never render
// it. The card stretches with the rail's 256–480px resize, but the artwork (a 3x export) is locked to
// its 456×186 1x size — wider than the card ever gets, so the centered crop just reveals more or less
// wallpaper — never scaled. The card chrome (radius, stroke, shadow) lives here in CSS, not the asset.
// Dismissal sticks per user via localStorage.
const DESKTOP_APP_CARD_DISMISSED_KEY = 'output:desktopAppCardDismissed';
const DESKTOP_APP_DOWNLOAD_URL =
  'https://echo-desktop-auto-update.s3.amazonaws.com/production/darwin/arm64/Idle-darwin-arm64-23.0.20260721192216.zip';

// Visibility lives outside the card so the rail can pad its scroll area to match: the card floats
// over the list (which scrolls underneath and clips against the card, not a straight line above it).
const useDesktopAppCard = () => {
  const storageKey = `${DESKTOP_APP_CARD_DISMISSED_KEY}:${currentUserId() || 'anon'}`;
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(storageKey) === 'true';
    } catch {
      return false;
    }
  });
  const dismiss = useCallback(() => {
    setDismissed(true);
    try {
      localStorage.setItem(storageKey, 'true');
    } catch {
      // noop, localStorage might be disabled
    }
  }, [storageKey]);
  return { visible: getMetaContent('app:variant') === 'web' && !dismissed, dismiss };
};

const WorkspaceRailDesktopAppCard = React.memo(function WorkspaceRailDesktopAppCard({ onDismiss }) {
  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-0 z-30 px-4 pb-4 max-sm:hidden">
      <div className="pointer-events-auto relative flex flex-col overflow-hidden rounded-[20px] border-[0.5px] border-white bg-gray-50 shadow-[0_2px_8px_rgba(0,0,0,0.08)] dark:bg-neutral-800">
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="absolute top-2 right-2 flex size-6 cursor-pointer items-center justify-center rounded-full bg-black/20 text-white hover:bg-black/30"
        >
          <X size={14} strokeWidth={2} />
        </button>
        <div className="flex h-[186px] justify-center overflow-hidden">
          <img src={DesktopAppPromoImage} alt="" className="h-[186px] w-[456px] max-w-none shrink-0" />
        </div>
        <div className="flex flex-col items-center px-6 pt-6 pb-5">
          <p className="w-[162px] max-w-full text-center text-[13px] leading-normal text-black dark:text-white">
            Get the Desktop App for a more seamless experience
          </p>
          <a
            href={DESKTOP_APP_DOWNLOAD_URL}
            className="mt-6 flex h-11 w-full items-center justify-center gap-1 rounded-full bg-black text-[13px] text-white hover:bg-gray-800"
          >
            <span>Download</span>
            {/* U+F8FF renders as the Apple mark on Apple platforms; elsewhere it may fall back to a box. */}
            <span aria-hidden className="font-['Helvetica'] text-xs leading-none">
              {''}
            </span>
            <span className="font-medium">macOS</span>
          </a>
        </div>
      </div>
    </div>
  );
});

const WorkspaceRail = React.memo(function WorkspaceRail({
  currentWorkspaceId,
  activeDirectMessageId = null,
  workspaceRailState = EMPTY_WORKSPACE_RAIL_STATE,
  userEmail = null,
  currentUser = null,
  onNewMessage,
  enableWorkspaceCycling = false,
  crossDomainLinks = false,
  condensed = false,
}) {
  const [location, navigate] = useLocation();
  const search = useSearch();
  const [railView, setRailView] = useState(WORKSPACE_RAIL_VIEW.WORKSPACES);
  // Drives the hairline under the toggle that fades in once rows scroll under it.
  const [railScrolled, setRailScrolled] = useState(false);
  const handleRailScroll = useCallback((event) => {
    setRailScrolled(event.currentTarget.scrollTop > 0);
  }, []);
  const [workspaceContextMenu, setWorkspaceContextMenu] = useState(null);
  const desktopAppCard = useDesktopAppCard();
  const { workspaceIds, visibleRailItems, latestMessagePreviews, previewsReady, initialRailSettled } = workspaceRailState;
  const { byWorkspaceId: workspaceUnreadSignals } = useWorkspaceUnreadSignals(workspaceIds);
  const unreadActivityCount = useUnreadActivityCount();
  const workspaceCyclingEnabled = !crossDomainLinks && enableWorkspaceCycling && getMetaContent('app:variant') === NATIVE_MACOS_APP_VARIANT;
  const currentRailItemKey = activeDirectMessageId
    ? `direct_message:${activeDirectMessageId}`
    : currentWorkspaceId
      ? `workspace:${currentWorkspaceId}`
      : null;
  const workspaceRailViewTabs = useMemo(
    () =>
      WORKSPACE_RAIL_VIEW_TABS.map((tab) => {
        if (tab.id !== WORKSPACE_RAIL_VIEW.ACTIVITY) return tab;

        return {
          ...tab,
          label: (
            <span className="relative inline-flex">
              <RailActivityIcon className="size-4" />
              <span className="sr-only">Activity</span>
              {unreadActivityCount > 0 ? <UnreadDot className="-top-0.5 -right-0.5 size-1.5 bg-idle-blue" /> : null}
            </span>
          ),
          ariaLabel: unreadActivityCount > 0 ? `Activity, ${unreadActivityCount} unread` : tab.ariaLabel,
        };
      }),
    [unreadActivityCount],
  );
  // `channelId` is supplied by rows that display a "#channel" prefix. Callers without one —
  // keyboard cycling, the post-hide DM fallback — keep the remembered-channel behaviour.
  const handleActivateWorkspacePickerItem = useCallback(
    function handleActivateWorkspacePickerItemCallback(item, channelId = null) {
      if (item.kind === 'direct_message') {
        activateDirectMessagePickerItem({ directMessage: item.directMessage, currentDirectMessageId: activeDirectMessageId, navigate });
        return;
      }

      activateWorkspacePickerItem({
        workspace: item.workspace,
        channelId,
        isCurrentWorkspace: railItemKey(item) === currentRailItemKey,
        navigate,
      });
    },
    [activeDirectMessageId, currentRailItemKey, navigate],
  );
  const handleCloseWorkspaceContextMenu = useCallback(function closeWorkspaceContextMenu() {
    setWorkspaceContextMenu(null);
  }, []);
  const handleOpenWorkspaceContextMenu = useCallback(
    function openWorkspaceContextMenu(event, item) {
      if (crossDomainLinks) return;

      window.dispatchEvent(new CustomEvent('output:toggle-context-menu', { detail: { isOpen: false } }));
      setWorkspaceContextMenu({ x: event.clientX, y: event.clientY, item });
    },
    [crossDomainLinks],
  );
  const openWorkspaceOptionsSection = useCallback(
    function openWorkspaceOptionsSectionCallback(workspace, section) {
      if (!workspace) return;

      const workspaceId = String(workspace.id);
      const isCurrentWorkspace = workspaceId === currentWorkspaceId;
      const nextSearchParams = new URLSearchParams(isCurrentWorkspace ? search : '');
      nextSearchParams.set('modal', 'workspace-options');
      nextSearchParams.set(WORKSPACE_OPTIONS_SECTION_QUERY_PARAM, section);

      const nextSearch = nextSearchParams.toString();
      if (isCurrentWorkspace) {
        navigate(nextSearch ? `${location}?${nextSearch}` : location);
        return;
      }

      requestMainLayoutPanel();
      navigate(`~${getWorkspacePickerPath(workspace)}?${nextSearch}`);
    },
    [currentWorkspaceId, location, navigate, search],
  );
  const handleOpenWorkspaceSettingsFromContextMenu = useCallback(
    function openWorkspaceSettingsFromContextMenu() {
      const workspace = workspaceContextMenu?.item?.workspace;
      handleCloseWorkspaceContextMenu();
      openWorkspaceOptionsSection(workspace, WORKSPACE_OPTIONS_SECTION.ROOT);
    },
    [handleCloseWorkspaceContextMenu, openWorkspaceOptionsSection, workspaceContextMenu?.item],
  );
  const handleOpenWorkspaceNotificationsFromContextMenu = useCallback(
    function openWorkspaceNotificationsFromContextMenu() {
      const workspace = workspaceContextMenu?.item?.workspace;
      handleCloseWorkspaceContextMenu();
      openWorkspaceOptionsSection(workspace, WORKSPACE_OPTIONS_SECTION.NOTIFICATIONS);
    },
    [handleCloseWorkspaceContextMenu, openWorkspaceOptionsSection, workspaceContextMenu?.item],
  );
  const handleOpenWorkspaceMembersFromContextMenu = useCallback(
    function openWorkspaceMembersFromContextMenu() {
      const workspace = workspaceContextMenu?.item?.workspace;
      handleCloseWorkspaceContextMenu();
      openWorkspaceOptionsSection(workspace, WORKSPACE_OPTIONS_SECTION.MEMBERS);
    },
    [handleCloseWorkspaceContextMenu, openWorkspaceOptionsSection, workspaceContextMenu?.item],
  );
  const handleHideDirectMessageFromContextMenu = useCallback(async () => {
    const directMessage = workspaceContextMenu?.item?.directMessage;
    if (!directMessage?.id) return;

    handleCloseWorkspaceContextMenu();

    try {
      const response = await apiDelete(`/direct_messages/${directMessage.id}`);
      const data = await response.json();

      if (!(response.ok && data.ok && data.data?.direct_message)) {
        logError('MainLayout', 'Failed to hide direct message', {
          directMessageId: directMessage.id,
          status: response.status,
          errors: data?.errors,
        });
        alert('Could not hide message. Please try again.');
        return;
      }

      await storeDirectMessagePayload(data.data.direct_message);
    } catch (error) {
      logError('MainLayout', 'Failed to hide direct message', { directMessageId: directMessage.id, error });
      alert('Could not hide message. Please try again.');
      return;
    }

    if (String(directMessage.id) !== String(activeDirectMessageId)) return;

    const nextItem = visibleRailItems.find((item) => railItemKey(item) !== `direct_message:${directMessage.id}`);
    if (nextItem) {
      handleActivateWorkspacePickerItem(nextItem);
    } else {
      window.location.href = rootDomainUrl('/');
    }
  }, [
    activeDirectMessageId,
    handleActivateWorkspacePickerItem,
    handleCloseWorkspaceContextMenu,
    visibleRailItems,
    workspaceContextMenu?.item,
  ]);

  useEffect(() => {
    if (railView !== WORKSPACE_RAIL_VIEW.WORKSPACES) handleCloseWorkspaceContextMenu();
  }, [handleCloseWorkspaceContextMenu, railView]);

  return (
    <>
      {workspaceCyclingEnabled && initialRailSettled ? (
        <WorkspaceCycleRouter
          items={visibleRailItems}
          currentItemKey={currentRailItemKey}
          onActivateItem={handleActivateWorkspacePickerItem}
        />
      ) : null}
      <div className="relative flex min-h-0 w-full flex-1 flex-col overflow-hidden">
        {/* Brand + compose row. On web the Idle wordmark sits left with the compose button right, aligned to the
          main column's header buttons. On native macOS the traffic lights own the top-left, so the wordmark is
          hidden and the compose button rides the traffic-light band at the sidebar's right edge. */}
        <div data-app-region="drag" className="flex shrink-0 items-center pt-4 pr-4 pl-4">
          {condensed ? (
            /* Condensed keeps the band's height with a centered wordmark; the compose button moves into the toggle's spot below. */
            <div className="flex h-9 w-full items-center justify-center">
              <IdleWordmarkIcon aria-hidden focusable="false" className="h-[18px] w-auto text-black dark:text-white native-macos:hidden" />
            </div>
          ) : (
            <>
              <IdleWordmarkIcon
                aria-hidden
                focusable="false"
                className="ml-2 h-[18px] w-auto text-black dark:text-white native-macos:hidden"
              />
              {/* Profile + compose share the right edge, gap-3 (12px) apart, the 20px avatar centered against the compose button. */}
              <div className="ml-auto flex items-center gap-3">
                <WorkspaceRailProfileButton userEmail={userEmail} user={currentUser} />
                <Tooltip label="New chat" className="flex shrink-0">
                  <NavButton
                    onClick={onNewMessage}
                    active={false}
                    aria-label="New chat"
                    className="bg-black shadow-[0_2px_4px_rgba(0,0,0,0.02)] hover:bg-gray-800 dark:hover:bg-gray-800"
                  >
                    <Plus className="size-4.5 text-white" strokeWidth={2} absoluteStrokeWidth />
                  </NavButton>
                </Tooltip>
              </div>
            </>
          )}
        </div>
        {/* The toggle-to-row gap splits across this pb (outside the scroll container, where scrolled
          rows clip) and the nav's scrollable pt below. */}
        <div className="relative shrink-0 px-3 pt-6 pb-3">
          {condensed ? (
            <div className="flex justify-center">
              <Tooltip label="New chat" className="flex shrink-0">
                <NavButton
                  onClick={onNewMessage}
                  active={false}
                  aria-label="New chat"
                  className="bg-black shadow-[0_2px_4px_rgba(0,0,0,0.02)] hover:bg-gray-800 dark:hover:bg-gray-800"
                >
                  <Plus className="size-4.5 text-white" strokeWidth={2} absoluteStrokeWidth />
                </NavButton>
              </Tooltip>
            </div>
          ) : (
            <SettingsTabs
              cornerRadius={48}
              tabs={workspaceRailViewTabs}
              activeTab={railView}
              setActiveTab={setRailView}
              className="w-full gap-[2px] bg-gray-300 p-[2px]"
              indicatorClassName="border-[0.5px] border-white bg-gray-50 dark:bg-neutral-800"
              tabClassName="flex-1 justify-center !px-[6px] !py-[8px] text-xs leading-none duration-75 [&_*]:transition-none"
              activeTabClassName="!text-black dark:!text-white"
              inactiveTabClassName="!bg-transparent !text-gray-500 hover:!text-black dark:!text-neutral-400 dark:hover:!text-white"
            />
          )}
          {/* Sits exactly on the scroll clip line; divider-colored, fades in while rows are scrolled under it. */}
          <div
            aria-hidden
            className={cn(
              'absolute inset-x-0 bottom-0 h-px bg-gray-300/60 transition-opacity duration-300 ease-fluid dark:bg-white/10',
              railScrolled && (condensed || railView === WORKSPACE_RAIL_VIEW.WORKSPACES) ? 'opacity-100' : 'opacity-0',
            )}
          />
        </div>

        {condensed || railView === WORKSPACE_RAIL_VIEW.WORKSPACES ? (
          <nav
            aria-label="Workspaces"
            aria-busy={!initialRailSettled}
            data-workspace-rail
            className={cn(
              'scrollbar-hidden flex min-h-0 w-full flex-1 flex-col items-center gap-[4px] overflow-y-auto px-3 pt-[10px] pb-4',
              // Clearance for the floating desktop-app card so the last rows can scroll above it.
              desktopAppCard.visible && !condensed && 'sm:pb-[360px]',
            )}
            onScroll={handleRailScroll}
          >
            {initialRailSettled
              ? visibleRailItems.map((item, index) => {
                  const workspaceId = railItemWorkspaceId(item);
                  const itemKey = railItemKey(item);
                  const isActive = itemKey === currentRailItemKey;
                  // Dividers separate flat rows; the ones touching the active row hide (the selected
                  // container replaces them) but keep their slot so selection never shifts the list.
                  const previousItemKey = index > 0 ? railItemKey(visibleRailItems[index - 1]) : null;
                  const showDivider = !isActive && previousItemKey !== currentRailItemKey;
                  return (
                    <React.Fragment key={itemKey}>
                      {index > 0 ? (
                        <div
                          aria-hidden
                          className={cn(
                            WORKSPACE_RAIL_DIVIDER_CLASS,
                            // Condensed rows are 76px wide; a 4px inset per side keeps the divider ~68px long.
                            condensed && 'w-[calc(100%-8px)]',
                            !showDivider && 'opacity-0',
                          )}
                        />
                      ) : null}
                      <WorkspacePickerItem
                        item={item}
                        isActive={isActive}
                        onActivateItem={handleActivateWorkspacePickerItem}
                        onOpenContextMenu={handleOpenWorkspaceContextMenu}
                        unreadSignal={workspaceUnreadSignals[workspaceId]}
                        latestMessagePreview={latestMessagePreviews?.[workspaceId]}
                        previewReady={previewsReady}
                        crossDomainLinks={crossDomainLinks}
                        condensed={condensed}
                      />
                    </React.Fragment>
                  );
                })
              : visibleRailItems.map((item, index) => (
                  <React.Fragment key={`skeleton:${railItemKey(item)}`}>
                    {index > 0 ? <div aria-hidden className={cn(WORKSPACE_RAIL_DIVIDER_CLASS, condensed && 'w-[calc(100%-8px)]')} /> : null}
                    <WorkspacePickerSkeletonItem item={item} />
                  </React.Fragment>
                ))}
          </nav>
        ) : (
          <ActivityFeed crossDomainLinks={crossDomainLinks} />
        )}
        {!condensed && railView === WORKSPACE_RAIL_VIEW.WORKSPACES && desktopAppCard.visible ? (
          <WorkspaceRailDesktopAppCard onDismiss={desktopAppCard.dismiss} />
        ) : null}
      </div>
      {workspaceContextMenu ? (
        <WorkspaceRailContextMenu
          position={workspaceContextMenu}
          onClose={handleCloseWorkspaceContextMenu}
          onSettings={handleOpenWorkspaceSettingsFromContextMenu}
          onNotifications={handleOpenWorkspaceNotificationsFromContextMenu}
          onManageMembers={handleOpenWorkspaceMembersFromContextMenu}
          onHideDirectMessage={workspaceContextMenu.item?.kind === 'direct_message' ? handleHideDirectMessageFromContextMenu : null}
        />
      ) : null}
    </>
  );
});

function MainLayoutContentLoading({ backgroundSource = null }) {
  const { isChatRoute } = useContext(WidgetsPaneControlsContext);
  const { hasWorkspaceBackgroundImage, workspaceBackgroundImageStyle } = useWorkspaceBackgroundImage(backgroundSource, isChatRoute);

  // Route providers can emit this fallback for one render even when Dexie is fast;
  // painting the rail-sourced image here prevents that render from becoming a flash.
  return (
    <div
      className={cn(
        'relative flex min-h-0 flex-1 overflow-hidden',
        hasWorkspaceBackgroundImage ? 'bg-gray-100' : 'bg-gray-50 dark:bg-neutral-900',
      )}
    >
      <WorkspaceBackgroundImageLayer style={workspaceBackgroundImageStyle} />
      <BootSkeleton />
    </div>
  );
}

// The account link belongs to the persistent shell, so it cannot depend on the
// route workspace provider that intentionally unmounts while a workspace hydrates.
// Shared-channel document loads can start as a shadow viewer; use the server
// bootstrap only until Dexie provides the signed-in user's shell rows.
export function useCurrentUserShell(workspaceId, initialShell = null) {
  const user = useCurrentUserRow();
  // Dexie returns a fresh row object on every emit; key the downstream query on a value-stable string.
  const ownedProfileIdsKey = getUserProfileIdsKey(user);
  const initialProfileId = initialShell?.profile?.id ? String(initialShell.profile.id) : '';
  const initialProfileWorkspaceId = initialShell?.profile?.workspace_id ? String(initialShell.profile.workspace_id) : '';
  const profile = useLiveQuery(
    async () => {
      if (!ownedProfileIdsKey) return null;
      const ownedProfileIds = ownedProfileIdsKey.split(':');
      if (workspaceId) {
        const workspaceCandidates = await db.profiles.bulkGet(ownedProfileIds.map((id) => [workspaceId, id]));
        const workspaceProfile = workspaceCandidates.find(Boolean);
        if (workspaceProfile) return workspaceProfile;
      }

      if (initialProfileWorkspaceId && initialProfileId) {
        const initialProfile = await db.profiles.get([initialProfileWorkspaceId, initialProfileId]);
        if (initialProfile) return initialProfile;
      }

      const workspaceIds = await db.workspaces.toCollection().primaryKeys();
      const fallbackKeys = workspaceIds.flatMap((candidateWorkspaceId) =>
        ownedProfileIds.map((ownedProfileId) => [String(candidateWorkspaceId), ownedProfileId]),
      );
      const fallbackCandidates = fallbackKeys.length ? await db.profiles.bulkGet(fallbackKeys) : [];
      return fallbackCandidates.find(Boolean) || null;
    },
    [initialProfileId, initialProfileWorkspaceId, ownedProfileIdsKey, workspaceId],
    null,
  );

  const userEmail = user?.email || initialShell?.user?.email || null;
  const resolvedProfile = profile || initialShell?.profile || null;
  const resolvedUser = user || initialShell?.user || null;

  return { userEmail, profile: resolvedProfile, user: resolvedUser };
}

export function MainLayoutFrame({
  workspaceId,
  activeDirectMessageId = null,
  workspaceRailState = EMPTY_WORKSPACE_RAIL_STATE,
  userEmail,
  currentUser,
  currentProfile,
  workspaceRailCrossDomainLinks = false,
  includeWidgetsPane = true,
  children,
}) {
  const [location, navigate] = useLocation();
  const currentWorkspaceId = workspaceId ? String(workspaceId) : null;
  const activeDirectMessage = useLiveQuery(
    () => (activeDirectMessageId ? db.direct_messages.get(String(activeDirectMessageId)) : null),
    [activeDirectMessageId],
    null,
  );
  const standardWorkspace = useLiveQuery(
    () => (currentWorkspaceId && !activeDirectMessageId ? db.workspaces.get(currentWorkspaceId) : null),
    [activeDirectMessageId, currentWorkspaceId],
    null,
  );
  const activeWorkspace = activeDirectMessageId ? directMessageWorkspaceSummary(activeDirectMessage) : standardWorkspace;
  const activeChannelId = channelIdFromMainLayoutPath(location);
  const availableWorkspaceIds = useMemo(
    () => new Set(workspaceRailState.visibleRailItems.map(railItemWorkspaceId)),
    [workspaceRailState.visibleRailItems],
  );
  const isBreakpointSm = useMediaQuery(BREAKPOINT_SM);
  const [mobileWidgetsOpen, setMobileWidgetsOpen] = useState(false);
  // The composer takes the whole shell over — rail included — so it lives here rather
  // than in the widgets pane, whose box stops at the rail's edge.
  // { workspaceId, widgetId, agentChatId } — the single owner of widget-building state.
  // widgetId/agentChatId are null for a plain "New" open, which is what lets the studio
  // resume the saved draft; a targeted open must never write over that pointer.
  const [widgetComposerSession, setWidgetComposerSession] = useState(null);
  const [spaceSetupSession, setSpaceSetupSession] = useState(null);
  // The setup flow refuses dismissal until the space has been named — see SpaceSetupFlow.
  const [isSpaceSetupDismissible, setIsSpaceSetupDismissible] = useState(false);
  const [newChatModalView, setNewChatModalView] = useState(null);
  const newWorkspaceOpenedFromMessageRef = useRef(false);
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);
  // Mobile has two horizontal gesture systems:
  // 1. The outer app shell uses CSS scroll snap to swipe between workspace rail and main content.
  // 2. The widget shelf is an overlaid right drawer with its own swipe-to-dismiss gesture.
  // While the shelf is open, lock the outer snap scroller so horizontal drags dismiss the shelf instead of moving the whole app.
  const mobileSnapEnabled = isBreakpointSm && !mobileWidgetsOpen;
  const { setScrollContainerRef } = useMobileSnapPanels({
    isEnabled: mobileSnapEnabled,
    getPanelIndex: getMainLayoutPanelIndex,
    initialPanel: 'main',
  });
  const { settings: layoutSettings, setLayoutPref } = useLayoutPreferences();
  const widgetsPaneOpen = isBreakpointSm ? mobileWidgetsOpen : layoutSettings.widgetsOpen;
  const mobileWidgetsActive = isBreakpointSm && mobileWidgetsOpen;
  // The composer hangs off the app shell so it can cover the rail's chat rows, then stops
  // short of the widgets pane. On mobile the shell is a two-panel snap scroller twice the
  // viewport wide, so it pins to the second panel instead of spanning the scroll width —
  // and the shelf there is an overlay, not a column, so nothing is reserved for it.
  const widgetComposerBounds = useMemo(
    function measureWidgetComposerBounds() {
      if (isBreakpointSm) return { left: '100dvw', right: 0 };
      return { left: 0, right: layoutSettings.widgetsOpen ? WIDGETS_PANE_WIDTH : 0 };
    },
    [isBreakpointSm, layoutSettings.widgetsOpen],
  );
  const isChatRoute = isChatMainLayoutPath(location);

  // The Add Emoji modal has its own dropzone; suspend the chat-wide one while it's
  // open so a drop aimed at the modal can't also post into the conversation.
  const [chatDropzoneSuspended, setChatDropzoneSuspended] = useState(false);
  useEffect(() => {
    const suspendChatDropzone = () => setChatDropzoneSuspended(true);
    const resumeChatDropzone = () => setChatDropzoneSuspended(false);
    window.addEventListener('output:internal:add-emoji-modal', suspendChatDropzone);
    window.addEventListener('output:internal:add-emoji-modal-closed', resumeChatDropzone);
    return () => {
      window.removeEventListener('output:internal:add-emoji-modal', suspendChatDropzone);
      window.removeEventListener('output:internal:add-emoji-modal-closed', resumeChatDropzone);
    };
  }, []);
  // The chat file dropzone lives up here — on the container that wraps the chat
  // column AND the widgets drawer — so a drag anywhere over either pane hits one
  // shared drop target with a single overlay spanning both. ChatSurface wires the
  // composer into it through ChatDropzoneContext.
  const chatDropzone = useComposerDropzone({ isInteractive: isChatRoute && !chatDropzoneSuspended });
  const dropTargetChannelId = activeChannelId || (activeWorkspace?.main_channel_id ? String(activeWorkspace.main_channel_id) : null);
  const dropTargetWorkspaceId = activeWorkspace?.id || currentWorkspaceId;
  const dropTargetChannel = useLiveQuery(
    () => (dropTargetWorkspaceId && dropTargetChannelId ? db.channels.get([dropTargetWorkspaceId, dropTargetChannelId]) : null),
    [dropTargetWorkspaceId, dropTargetChannelId],
    null,
  );
  const chatDropzoneContextValue = useMemo(
    () => ({
      dropzoneHelpers: chatDropzone.dropzoneHelpers,
      handleDropzoneStateChange: chatDropzone.handleDropzoneStateChange,
      handleUploadCountChange: chatDropzone.handleUploadCountChange,
    }),
    [chatDropzone.dropzoneHelpers, chatDropzone.handleDropzoneStateChange, chatDropzone.handleUploadCountChange],
  );
  // Painted once here so the chat column and widgets pane share a single flat
  // background image instead of each pane restarting its own bg-cover crop.
  const { hasWorkspaceBackgroundImage: hasSpanningBackgroundImage, workspaceBackgroundImageStyle: spanningBackgroundImageStyle } =
    useWorkspaceBackgroundImage(activeWorkspace, isChatRoute);
  const handleToggleWidgets = useCallback(
    function toggleWidgets() {
      if (isBreakpointSm) {
        setMobileWidgetsOpen((previousOpen) => !previousOpen);
        return;
      }

      setLayoutPref('widgetsOpen', (previousOpen) => !previousOpen);
    },
    [isBreakpointSm, setLayoutPref],
  );
  const widgetsPaneControlsContextValue = useMemo(
    () => ({
      isChatRoute,
      mobileWidgetsActive,
      mobileWidgetsOpen,
      setMobileWidgetsOpen,
      toggleWidgets: handleToggleWidgets,
      widgetsPaneOpen,
    }),
    [handleToggleWidgets, isChatRoute, mobileWidgetsActive, mobileWidgetsOpen, setMobileWidgetsOpen, widgetsPaneOpen],
  );
  const handleOpenAccountModal = useCallback(function openAccountModal() {
    setIsAccountModalOpen(true);
  }, []);
  const handleCloseAccountModal = useCallback(function closeAccountModal() {
    setIsAccountModalOpen(false);
  }, []);

  useEffect(
    function listenForAccountOptionsModalEvent() {
      window.addEventListener(ACCOUNT_OPTIONS_MODAL_EVENT, handleOpenAccountModal);

      return function removeAccountOptionsModalEventListener() {
        window.removeEventListener(ACCOUNT_OPTIONS_MODAL_EVENT, handleOpenAccountModal);
      };
    },
    [handleOpenAccountModal],
  );

  // Flows that leave the app (e.g. the Google contacts OAuth redirect) reopen the
  // account modal on return via this marker param.
  useEffect(
    function openAccountModalFromReturnMarker() {
      const url = new URL(window.location.href);
      if (url.searchParams.get('account_options') !== '1') return;

      url.searchParams.delete('account_options');
      window.history.replaceState(window.history.state, '', url.toString());
      handleOpenAccountModal();
    },
    [handleOpenAccountModal],
  );
  // New Chat and Create Group are two views of ONE modal portal: separate portals
  // each carry their own backdrop, so hopping between them faded one backdrop out
  // while the other faded in — a visible flash. One portal stays mounted across the
  // hop, and the Create Group back chevron can return to the New Chat view.
  const handleOpenNewMessageModal = useCallback(function openNewMessageModal() {
    setNewChatModalView(NEW_CHAT_MODAL_VIEW.MESSAGE);
  }, []);
  const handleCloseNewChatModal = useCallback(function closeNewChatModal() {
    setNewChatModalView(null);
  }, []);
  const handleNewChatModalOpenChange = useCallback(
    function handleNewChatModalOpenChangeCallback(nextOpen) {
      if (!nextOpen) handleCloseNewChatModal();
    },
    [handleCloseNewChatModal],
  );
  // Opened from the New Chat modal's "Create a group": back returns to New Chat.
  const handleOpenNewWorkspaceFromNewMessage = useCallback(function openNewWorkspaceFromNewMessage() {
    newWorkspaceOpenedFromMessageRef.current = true;
    setNewChatModalView(SPACE_SETUP_GENERATION_ENABLED ? NEW_CHAT_MODAL_VIEW.DESCRIBE : NEW_CHAT_MODAL_VIEW.GROUP);
  }, []);
  const mainLayoutActionsContextValue = useMemo(() => ({ openNewMessage: handleOpenNewMessageModal }), [handleOpenNewMessageModal]);
  const handleBackFromNewWorkspace = useCallback(function backFromNewWorkspace() {
    setNewChatModalView(newWorkspaceOpenedFromMessageRef.current ? NEW_CHAT_MODAL_VIEW.MESSAGE : null);
  }, []);
  const buildNewWorkspaceUrl = useCallback(
    function buildNewWorkspaceUrlCallback(workspace) {
      return getWorkspacePickerPath(workspace, { crossDomainLinks: workspaceRailCrossDomainLinks });
    },
    [workspaceRailCrossDomainLinks],
  );
  const handleWorkspaceCreated = useCallback(
    async function handleWorkspaceCreatedCallback(createdWorkspacePayload, targetPath) {
      if (!createdWorkspacePayload?.workspace?.id || !targetPath) return;

      if (workspaceRailCrossDomainLinks || targetPath.includes('://')) {
        assignDocument(targetPath, 'workspace-switch');
        return;
      }

      const liveJournal = window.livejournal;
      if (!liveJournal?.hydrateCreatedWorkspace) {
        assignDocument(targetPath, 'workspace-switch');
        return;
      }

      await liveJournal.hydrateCreatedWorkspace(createdWorkspacePayload);
      // Dexie has the rows now, but the destination route's live queries still
      // start on their loading sentinel. Prime the provider with the same POST
      // payload so the new workspace can paint on the first route render.
      primeCreatedWorkspaceRoute(createdWorkspacePayload);
      handleCloseNewChatModal();
      requestMainLayoutPanel();
      navigate(`~${targetPath}`);
    },
    [handleCloseNewChatModal, navigate, workspaceRailCrossDomainLinks],
  );
  // Screens C-F run against the space the describe step just created, so they only
  // mount once the route has actually settled on that workspace.
  const handleSpaceSetupStarted = useCallback(
    async function spaceSetupStarted(createdWorkspacePayload, targetPath) {
      const workspaceId = createdWorkspacePayload?.workspace?.id;
      await handleWorkspaceCreated(createdWorkspacePayload, targetPath);
      if (workspaceId) {
        setIsSpaceSetupDismissible(false);
        setSpaceSetupSession({ workspaceId: String(workspaceId) });
      }
    },
    [handleWorkspaceCreated],
  );
  const handleCloseSpaceSetup = useCallback(
    function closeSpaceSetup() {
      if (!isSpaceSetupDismissible) return;
      setSpaceSetupSession(null);
    },
    [isSpaceSetupDismissible],
  );
  // Backing out before the space is named discards it. The space only exists at that
  // point because generation needed somewhere to hang, so leaving it behind would put an
  // unfinished space in the sidebar that nothing can finish setting up.
  const handleAbandonSpaceSetup = useCallback(
    async function abandonSpaceSetup() {
      const workspaceId = spaceSetupSession?.workspaceId;
      setSpaceSetupSession(null);
      if (!workspaceId) return;

      try {
        const response = await apiDelete(`/workspaces/${workspaceId}`);
        const data = await response.json();
        if (!(response.ok && data?.ok)) throw new Error('discard_failed');
      } catch (error) {
        // The space survives; it is a stray rather than a broken state, and the user can
        // still delete it from settings.
        logError('MainLayout', 'Failed to discard an abandoned space', { message: error?.message });
        return;
      }

      // Same teardown the leave/delete paths use: drops it from the rail and routes away.
      window.dispatchEvent(new CustomEvent('output:kill-workspace', { detail: { workspaceId } }));
    },
    [spaceSetupSession?.workspaceId],
  );
  const handleWorkspaceRailWidthChange = useCallback(
    function handleWorkspaceRailWidthChangeCallback(width) {
      setLayoutPref('workspaceRailWidth', width);
    },
    [setLayoutPref],
  );
  // A cross-workspace open (banner click) sets the session before navigating, so the
  // drop-on-switch effect below has to tolerate a session whose workspace the route
  // has not caught up to yet — it only clears once a *different* workspace settles.
  const handleOpenWidgetComposer = useCallback(
    function openWidgetComposer(target = null) {
      setWidgetComposerSession({
        workspaceId: target?.workspaceId ? String(target.workspaceId) : currentWorkspaceId,
        widgetId: target?.widgetId ? String(target.widgetId) : null,
        agentChatId: target?.agentChatId ? String(target.agentChatId) : null,
        // Which pane the studio should land on. Only 'browse' is used today; anything
        // else opens on the prompt.
        view: target?.view || null,
      });
    },
    [currentWorkspaceId],
  );
  const handleCloseWidgetComposer = useCallback(function closeWidgetComposer() {
    setWidgetComposerSession(null);
  }, []);

  useEffect(
    function dropComposerOnWorkspaceSwitch() {
      setWidgetComposerSession((session) => (!session || session.workspaceId === currentWorkspaceId ? session : null));
    },
    [currentWorkspaceId],
  );
  const workspaceRailResize = useHorizontalResize({
    initialWidth: layoutSettings.workspaceRailWidth,
    minWidth: WORKSPACE_RAIL_LAYOUT_BOUNDS.minWidth,
    maxWidth: WORKSPACE_RAIL_LAYOUT_BOUNDS.maxWidth,
    direction: 'right',
    onWidthChange: handleWorkspaceRailWidthChange,
    snap: WORKSPACE_RAIL_SNAP,
  });
  const isWorkspaceRailCondensed = !isBreakpointSm && workspaceRailResize.width === WORKSPACE_RAIL_LAYOUT_BOUNDS.condensedWidth;
  const handleOutputInternalUpdateRoute = useCallback(
    (event) => {
      const to = event?.detail?.to;
      if (!to) return;

      if (!workspaceRailCrossDomainLinks) {
        navigate(to);
        return;
      }

      const target = getAppRouteTargetPath(to);
      if (target) assignDocument(target.includes('://') ? target : rootDomainUrl(target), 'workspace-switch');
    },
    [navigate, workspaceRailCrossDomainLinks],
  );

  useEffect(() => {
    window.addEventListener('output:internal:update-route', handleOutputInternalUpdateRoute);

    return () => {
      window.removeEventListener('output:internal:update-route', handleOutputInternalUpdateRoute);
    };
  }, [handleOutputInternalUpdateRoute]);

  return (
    <FloatingTree>
      <KeyMasterProvider>
        <RichdownComposerFocusRouterProvider>
          {/* Keep the app shell mounted across workspace hydration so switching spaces feels like an in-place content swap. */}
          <div
            ref={isBreakpointSm ? setScrollContainerRef : undefined}
            className={cn(
              // sm:overflow-clip, not overflow-hidden: hidden would make this a scroll
              // container, and the widget composer parks off to the right at
              // translate-x-full — focusing into it scrolled the whole shell sideways.
              // Clip stops the document scrolling without clipping the composer's shadow,
              // which has to fall across the widgets pane well inside these bounds.
              'relative flex size-full min-w-0 bg-gray-200 sm:overflow-clip',
              mobileSnapEnabled
                ? 'max-sm:scrollbar-hidden max-sm:snap-x max-sm:snap-mandatory max-sm:overflow-x-auto max-sm:overflow-y-hidden max-sm:overscroll-x-contain'
                : 'max-sm:overflow-hidden',
            )}
          >
            <aside
              style={isBreakpointSm ? undefined : { width: workspaceRailResize.width }}
              className={cn(
                'relative flex h-full min-h-0 flex-none flex-col overflow-hidden border-r-[0.5px] border-r-white select-none max-sm:w-dvw max-sm:snap-start max-sm:border-r-0 max-sm:p-1',
                !isBreakpointSm && !workspaceRailResize.isResizing && 'transition-sizes',
              )}
            >
              <WorkspaceRail
                currentWorkspaceId={currentWorkspaceId}
                activeDirectMessageId={activeDirectMessageId}
                workspaceRailState={workspaceRailState}
                userEmail={userEmail}
                currentUser={currentUser}
                onNewMessage={handleOpenNewMessageModal}
                enableWorkspaceCycling
                crossDomainLinks={workspaceRailCrossDomainLinks}
                condensed={isWorkspaceRailCondensed}
              />
              <ResizeHandle
                position="right"
                isResizing={workspaceRailResize.isResizing}
                currentWidth={workspaceRailResize.width}
                minWidth={WORKSPACE_RAIL_LAYOUT_BOUNDS.minWidth}
                maxWidth={WORKSPACE_RAIL_LAYOUT_BOUNDS.maxWidth}
                className="max-sm:hidden"
                data-app-region="none"
                {...workspaceRailResize.getHandleProps(isBreakpointSm)}
              />
            </aside>
            <WorkspaceRailWidthContext.Provider value={isBreakpointSm ? 0 : workspaceRailResize.width}>
              <MainLayoutActionsContext.Provider value={mainLayoutActionsContextValue}>
                <WidgetsPaneControlsContext.Provider value={widgetsPaneControlsContextValue}>
                  <div className="flex min-w-0 flex-1 flex-col overflow-hidden max-sm:w-dvw max-sm:flex-none max-sm:snap-start max-sm:p-1">
                    <div
                      {...chatDropzone.getRootProps({
                        className: cn(
                          // overflow-clip, not overflow-hidden: hidden still makes this a scroll
                          // container, so focusing into anything parked off to the side (a pane
                          // at translate-x-full) scrolled the whole region sideways and back.
                          'relative flex min-h-0 min-w-0 flex-1 overflow-clip',
                          !hasSpanningBackgroundImage && 'bg-gray-50',
                          hasSpanningBackgroundImage && WORKSPACE_BACKGROUND_IMAGE_CLASS_NAME,
                        ),
                      })}
                    >
                      <WorkspaceBackgroundImageLayer style={spanningBackgroundImageStyle} />
                      {/* One header gradient across chat + drawer: two side-by-side backdrop-filter
                          elements can't blend (each blurs only its own region), so the blur must be a
                          single element to be seamless. z-9 sits above pane content, below the navs (z-10+). */}
                      <div aria-hidden className="header-gradient" style={{ zIndex: 9 }} />
                      <DropOverlay
                        isVisible={chatDropzone.showDropzoneOverlay}
                        label={dropTargetChannel ? `Upload to ${getChannelDisplayName(dropTargetChannel)}` : 'Upload'}
                        onImage={hasSpanningBackgroundImage}
                      />
                      <div className="relative flex min-w-0 flex-1 flex-col overflow-hidden">
                        <ChatDropzoneContext.Provider value={chatDropzoneContextValue}>{children}</ChatDropzoneContext.Provider>
                      </div>
                      {includeWidgetsPane ? (
                        <PersistentWidgetsPane
                          activeChannelId={activeChannelId}
                          activeDirectMessageId={activeDirectMessageId}
                          activeWorkspace={activeWorkspace}
                          availableWorkspaceIds={availableWorkspaceIds}
                          currentProfile={currentProfile}
                          isBreakpointSm={isBreakpointSm}
                          isChatRoute={isChatRoute}
                          layoutSettings={layoutSettings}
                          location={location}
                          mobileWidgetsOpen={mobileWidgetsOpen}
                          navigate={navigate}
                          onOpenWidgetComposer={handleOpenWidgetComposer}
                          setLayoutPref={setLayoutPref}
                          setMobileWidgetsOpen={setMobileWidgetsOpen}
                          widgetsPaneOpen={widgetsPaneOpen}
                          workspaceId={currentWorkspaceId}
                        />
                      ) : null}
                    </div>
                    <NagBar />
                  </div>
                </WidgetsPaneControlsContext.Provider>
              </MainLayoutActionsContext.Provider>
            </WorkspaceRailWidthContext.Provider>
            {/* Anchored to the app shell, not the chat container: the takeover slides over
                the rail's chat rows and the chat column, and `bounds` leaves the widgets
                pane uncovered so the board stays live beside the builder. */}
            {includeWidgetsPane ? (
              <WidgetComposerOverlay
                open={Boolean(currentWorkspaceId) && widgetComposerSession?.workspaceId === currentWorkspaceId}
                session={widgetComposerSession}
                workspaceId={currentWorkspaceId}
                directMessageId={activeDirectMessageId}
                channelId={activeChannelId}
                bounds={widgetComposerBounds}
                onClose={handleCloseWidgetComposer}
              />
            ) : null}
          </div>
          <ModalPortal
            open={newChatModalView !== null}
            onOpenChange={handleNewChatModalOpenChange}
            keychainId={newChatModalView === NEW_CHAT_MODAL_VIEW.GROUP ? KEYCHAIN.MODAL__NEW_WORKSPACE : KEYCHAIN.MODAL__NEW_MESSAGE}
            className="h-[640px] max-h-[calc(100dvh-32px)] max-w-[512px]"
          >
            {newChatModalView === NEW_CHAT_MODAL_VIEW.DESCRIBE ? (
              <NewSpaceDescribeModal
                onClose={handleBackFromNewWorkspace}
                onSpaceSetupStarted={handleSpaceSetupStarted}
                workspaceUrlForWorkspace={buildNewWorkspaceUrl}
              />
            ) : newChatModalView === NEW_CHAT_MODAL_VIEW.GROUP ? (
              <NewWorkspaceModal
                onClose={handleBackFromNewWorkspace}
                onWorkspaceCreated={handleWorkspaceCreated}
                workspaceUrlForWorkspace={buildNewWorkspaceUrl}
                libraryWorkspaceId={currentWorkspaceId}
                libraryDirectMessageId={activeDirectMessageId}
              />
            ) : (
              <NewMessageModal onClose={handleCloseNewChatModal} onNewWorkspaceSelected={handleOpenNewWorkspaceFromNewMessage} />
            )}
          </ModalPortal>
          <ModalPortal
            open={Boolean(spaceSetupSession) && currentWorkspaceId === spaceSetupSession?.workspaceId}
            onClose={handleCloseSpaceSetup}
            keychainId={KEYCHAIN.MODAL__SPACE_SETUP}
            className="h-[640px] max-h-[calc(100dvh-32px)] max-w-[512px]"
          >
            <SpaceSetupFlow
              workspaceId={spaceSetupSession?.workspaceId}
              onClose={handleCloseSpaceSetup}
              onAbandon={handleAbandonSpaceSetup}
              onDismissibleChange={setIsSpaceSetupDismissible}
              onOpenWidgetComposer={handleOpenWidgetComposer}
            />
          </ModalPortal>
          <ModalPortal
            open={isAccountModalOpen}
            onClose={handleCloseAccountModal}
            keychainId={KEYCHAIN.MODAL__ACCOUNT_OPTIONS}
            size="settings"
            className="md:!h-160 md:!max-h-[calc(100dvh-2rem)] md:!max-w-128"
          >
            <AccountOptionsModal
              open={isAccountModalOpen}
              onClose={handleCloseAccountModal}
              userEmail={userEmail}
              user={currentUser}
              currentProfile={currentProfile}
            />
          </ModalPortal>
        </RichdownComposerFocusRouterProvider>
      </KeyMasterProvider>
    </FloatingTree>
  );
}

const ACTIVE_CONFERENCE_STATUSES = new Set(['joining', 'joined']);

function useMainPanelRoundedClasses() {
  const appVariant = getMetaContent('app:variant');
  return useMemo(() => {
    // customize the radius based on the window radius
    if (appVariant === NATIVE_MACOS_APP_VARIANT) return ['rounded-bl-xl', 'rounded-br-xl'];
    const browser = new UAParser().getBrowser().name;
    if (browser === 'Chrome') return ['rounded-bl-2xl', 'rounded-br-2xl'];
    if (browser === 'Safari') return ['rounded-bl-3xl', 'rounded-br-3xl'];

    // default to same as top radii
    return ['rounded-bl-lg', 'rounded-br-lg'];
  }, [appVariant]);
}

function RetainedWorkspacePaneContent({
  activePaneBodyRef,
  activeChannelId,
  galleryFocusSlug,
  isActive,
  isDirectMessage,
  widgetsPaneView,
  widgetsEditing = false,
}) {
  const { workspaceId, profile: currentProfile, mainChannelId } = useRouteWorkspace();
  // Record widgets the viewer actually sees on the pane; this is what decays
  // the feed's widget announcement chips from tinted to quiet.
  useWidgetViewedObserver(activePaneBodyRef, isActive);
  const canShowWidgetGallery = !isDirectMessage && Boolean(currentProfile);
  const channelId = activeChannelId || mainChannelId || null;
  const effectiveWidgetsPaneView = useMemo(
    function resolveWidgetsPaneView() {
      if (widgetsPaneView === WIDGETS_PANE_VIEW.GALLERY && canShowWidgetGallery) return WIDGETS_PANE_VIEW.GALLERY;
      return WIDGETS_PANE_VIEW.BOARD;
    },
    [canShowWidgetGallery, widgetsPaneView],
  );

  return (
    <div
      ref={isActive ? activePaneBodyRef : undefined}
      data-widget-workspace-id={workspaceId}
      className="pointer-events-none absolute inset-y-0 right-0 -left-10 scrollbar-thin overflow-y-auto pt-[76px] pl-10"
    >
      <Activity mode={effectiveWidgetsPaneView === WIDGETS_PANE_VIEW.BOARD ? 'visible' : 'hidden'}>
        <BoardView widgetsEditing={widgetsEditing} />
      </Activity>
      {effectiveWidgetsPaneView === WIDGETS_PANE_VIEW.GALLERY ? (
        <WidgetGalleryView channelId={channelId} focusSlug={galleryFocusSlug} />
      ) : null}
    </div>
  );
}

function RetainedWorkspacePaneEntry(props) {
  const { directMessageId, isActive, workspaceId } = props;
  const loadingFallback = (
    <div className="absolute inset-0 flex items-center justify-center pt-[76px]" role="status" aria-label="Loading board">
      <LoadingWrapper interactive={false} />
    </div>
  );

  const content = <RetainedWorkspacePaneContent {...props} isDirectMessage={Boolean(directMessageId)} />;
  const provider = directMessageId ? (
    <DirectMessageRouteProvider directMessageId={directMessageId} includeEntitySnapshots={false} loadingFallback={loadingFallback}>
      {content}
    </DirectMessageRouteProvider>
  ) : (
    <RouteWorkspaceProvider workspaceId={workspaceId} includeEntitySnapshots={false} loadingFallback={loadingFallback}>
      {content}
    </RouteWorkspaceProvider>
  );

  return <Activity mode={isActive ? 'visible' : 'hidden'}>{provider}</Activity>;
}

function useMobileWidgetShelfGesture({ isBreakpointSm, mobileWidgetsOpen, setMobileWidgetsOpen }) {
  const [mobileWidgetShelfDragX, setMobileWidgetShelfDragX] = useState(0);
  const [mobileWidgetShelfDragging, setMobileWidgetShelfDragging] = useState(false);
  const mobileWidgetShelfGestureRef = useRef(null);

  const resetMobileWidgetShelfGesture = useCallback(function resetMobileWidgetShelfGesture() {
    mobileWidgetShelfGestureRef.current = null;
    setMobileWidgetShelfDragging(false);
    setMobileWidgetShelfDragX(0);
  }, []);

  const handleWidgetShelfPointerDown = useCallback(
    function handleWidgetShelfPointerDown(event) {
      if (!isBreakpointSm || !mobileWidgetsOpen) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      mobileWidgetShelfGestureRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        lastX: event.clientX,
        lastTime: performance.now(),
        velocity: 0,
        axis: null,
        hasCapture: false,
      };
      setMobileWidgetShelfDragging(false);
      setMobileWidgetShelfDragX(0);
    },
    [isBreakpointSm, mobileWidgetsOpen],
  );

  const handleWidgetShelfPointerMove = useCallback(function handleWidgetShelfPointerMove(event) {
    const gesture = mobileWidgetShelfGestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;

    const dx = event.clientX - gesture.startX;
    const dy = event.clientY - gesture.startY;
    const absX = Math.abs(dx);
    const absY = Math.abs(dy);

    if (!gesture.axis) {
      if (absX < WIDGET_SHELF_DRAG_INTENT_PX && absY < WIDGET_SHELF_DRAG_INTENT_PX) return;
      gesture.axis = absX > absY && dx > 0 ? 'horizontal' : 'vertical';
    }

    if (gesture.axis !== 'horizontal') return;

    if (!gesture.hasCapture) {
      try {
        event.currentTarget.setPointerCapture?.(event.pointerId);
        gesture.hasCapture = true;
      } catch {
        // Pointer capture is best-effort; the threshold still works without it.
      }
    }

    event.preventDefault();
    const nextDragX = Math.max(0, dx);
    const now = performance.now();
    gesture.velocity = Math.max(0, (event.clientX - gesture.lastX) / Math.max(1, now - gesture.lastTime));
    gesture.lastX = event.clientX;
    gesture.lastTime = now;
    setMobileWidgetShelfDragging(true);
    setMobileWidgetShelfDragX(nextDragX);
  }, []);

  const handleWidgetShelfPointerEnd = useCallback(
    function handleWidgetShelfPointerEnd(event) {
      const gesture = mobileWidgetShelfGestureRef.current;
      if (!gesture || gesture.pointerId !== event.pointerId) return;

      const dx = Math.max(0, event.clientX - gesture.startX);
      const dismissDistance = Math.min(WIDGETS_PANE_WIDTH * WIDGET_SHELF_DISMISS_FRACTION, WIDGET_SHELF_DISMISS_MAX_PX);
      const shouldDismiss =
        gesture.axis === 'horizontal' &&
        (dx >= dismissDistance || (dx > WIDGET_SHELF_DRAG_INTENT_PX * 2 && gesture.velocity > WIDGET_SHELF_DISMISS_FLICK_VELOCITY));

      if (gesture.hasCapture) {
        try {
          event.currentTarget.releasePointerCapture?.(event.pointerId);
        } catch {
          // If the browser already released capture, there is nothing left to clean up.
        }
      }

      mobileWidgetShelfGestureRef.current = null;
      setMobileWidgetShelfDragging(false);
      setMobileWidgetShelfDragX(0);
      if (shouldDismiss) setMobileWidgetsOpen(false);
    },
    [setMobileWidgetsOpen],
  );

  useEffect(() => {
    if (mobileWidgetsOpen) return;
    resetMobileWidgetShelfGesture();
  }, [mobileWidgetsOpen, resetMobileWidgetShelfGesture]);

  return {
    mobileWidgetShelfDragX,
    mobileWidgetShelfDragging,
    handleWidgetShelfPointerDown,
    handleWidgetShelfPointerMove,
    handleWidgetShelfPointerUp: handleWidgetShelfPointerEnd,
    handleWidgetShelfPointerCancel: handleWidgetShelfPointerEnd,
  };
}

function useRetainedWidgetWorkspaces({ activeWorkspaceId, activeDirectMessageId, availableWorkspaceIds }) {
  const [retainedWorkspaceIds, setRetainedWorkspaceIds] = useState(() =>
    activeWorkspaceId ? retainWidgetWorkspace([], activeWorkspaceId) : [],
  );
  const [workspaceMountOrder, setWorkspaceMountOrder] = useState(() => (activeWorkspaceId ? [String(activeWorkspaceId)] : []));
  const [directMessageIdsByWorkspace, setDirectMessageIdsByWorkspace] = useState({});
  const renderedWorkspaceIds = useMemo(
    () => retainWidgetWorkspace(retainedWorkspaceIds, activeWorkspaceId),
    [activeWorkspaceId, retainedWorkspaceIds],
  );
  // LRU order changes on every activation. Rendering in that order would make React
  // move keyed iframe subtrees in the DOM, which reloads their browsing contexts.
  // Keep recency for eviction only and append new mounted entries without
  // relocating any iframe subtree that is already in the document.
  const mountedWorkspaceIds = useMemo(
    () => stableWidgetWorkspaceRenderOrder(workspaceMountOrder, renderedWorkspaceIds),
    [renderedWorkspaceIds, workspaceMountOrder],
  );

  useEffect(() => {
    setRetainedWorkspaceIds((previousWorkspaceIds) => {
      const retainedIds = retainWidgetWorkspace(previousWorkspaceIds, activeWorkspaceId);
      const prunedIds = pruneWidgetWorkspaces(retainedIds, availableWorkspaceIds, activeWorkspaceId);
      return previousWorkspaceIds.length === prunedIds.length && previousWorkspaceIds.every((id, index) => id === prunedIds[index])
        ? previousWorkspaceIds
        : prunedIds;
    });
  }, [activeWorkspaceId, availableWorkspaceIds]);

  useEffect(() => {
    setWorkspaceMountOrder((previousMountOrder) => {
      const nextMountOrder = stableWidgetWorkspaceRenderOrder(previousMountOrder, renderedWorkspaceIds);
      return previousMountOrder.length === nextMountOrder.length &&
        previousMountOrder.every((workspaceId, index) => workspaceId === nextMountOrder[index])
        ? previousMountOrder
        : nextMountOrder;
    });
  }, [renderedWorkspaceIds]);

  useEffect(() => {
    setDirectMessageIdsByWorkspace((previousDirectMessageIds) => {
      const prunedDirectMessageIds = pruneDirectMessageIdsByWorkspace(previousDirectMessageIds, mountedWorkspaceIds);
      if (!activeWorkspaceId) return prunedDirectMessageIds;

      const directMessageId = activeDirectMessageId ? String(activeDirectMessageId) : null;
      if (prunedDirectMessageIds[activeWorkspaceId] === directMessageId) return prunedDirectMessageIds;

      return { ...prunedDirectMessageIds, [activeWorkspaceId]: directMessageId };
    });
  }, [activeDirectMessageId, activeWorkspaceId, mountedWorkspaceIds]);

  return { mountedWorkspaceIds, directMessageIdsByWorkspace };
}

function PersistentWidgetsPane({
  activeChannelId,
  activeDirectMessageId,
  activeWorkspace,
  availableWorkspaceIds,
  currentProfile,
  isBreakpointSm,
  isChatRoute,
  layoutSettings,
  location,
  mobileWidgetsOpen,
  navigate,
  onOpenWidgetComposer,
  setLayoutPref,
  setMobileWidgetsOpen,
  widgetsPaneOpen,
  workspaceId,
}) {
  const activeWorkspaceId = workspaceId ? String(workspaceId) : null;
  const { mountedWorkspaceIds, directMessageIdsByWorkspace } = useRetainedWidgetWorkspaces({
    activeWorkspaceId,
    activeDirectMessageId,
    availableWorkspaceIds,
  });
  const [widgetsPaneView, setWidgetsPaneView] = useState(WIDGETS_PANE_VIEW.BOARD);
  const [galleryFocusSlug, setGalleryFocusSlug] = useState(null);
  const {
    mobileWidgetShelfDragX,
    mobileWidgetShelfDragging,
    handleWidgetShelfPointerDown,
    handleWidgetShelfPointerMove,
    handleWidgetShelfPointerUp,
    handleWidgetShelfPointerCancel,
  } = useMobileWidgetShelfGesture({ isBreakpointSm, mobileWidgetsOpen, setMobileWidgetsOpen });
  const activePaneBodyRef = useRef(null);
  const appliedWidgetGalleryDeepLinkRef = useRef(null);
  const previousLocationRef = useRef(location);
  const search = useSearch();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const requestedWidgetsPane = searchParams.get(WIDGETS_PANE_QUERY_PARAM);
  const requestedWidgetSlug = searchParams.get(WIDGET_QUERY_PARAM);
  const canShowWidgetGallery = !activeDirectMessageId && Boolean(currentProfile);
  const roundedClasses = useMainPanelRoundedClasses();
  const { hasWorkspaceBackgroundImage, workspaceBackgroundImageStyle } = useWorkspaceBackgroundImage(activeWorkspace, isChatRoute);
  // Only the count matters here (the header's Edit half hides on an empty board),
  // so this skips BoardView's widget join. Unresolved counts as empty: Edit
  // appearing a frame after the tiles beats it flashing over the empty state.
  const boardWidgetCount = useLiveQuery(
    () => (activeWorkspaceId ? db.board_items.where('[workspace_id+boardable_type]').equals([activeWorkspaceId, 'widget']).count() : 0),
    [activeWorkspaceId],
    0,
  );

  useEffect(() => {
    setWidgetsPaneView(WIDGETS_PANE_VIEW.BOARD);
    setGalleryFocusSlug(null);
  }, [activeWorkspaceId]);

  // One surface owns widget building now, so opening it is just opening the studio —
  // there is no second host left to race it for the bootstrapped widget chat.
  const handleOpenWidgetComposer = useCallback(
    function openWidgetComposer() {
      onOpenWidgetComposer();
    },
    [onOpenWidgetComposer],
  );
  // Browsing happens in the studio's grid, not the pane's gallery view — the catalog
  // lives beside the builder now, so the board just opens the studio on that pane.
  const handleBrowseWidgetLibrary = useCallback(
    function browseWidgetLibrary() {
      onOpenWidgetComposer({ view: 'browse' });
    },
    [onOpenWidgetComposer],
  );
  const handleOpenWidgetBuilder = useCallback(
    async function openWidgetBuilder(event) {
      const targetWorkspaceId = event?.detail?.workspaceId ? String(event.detail.workspaceId) : null;
      const widgetId = event?.detail?.widgetId ? String(event.detail.widgetId) : null;
      const agentChatId = event?.detail?.agentChatId ? String(event.detail.agentChatId) : null;
      // `mode: 'create'` is an untargeted open (the empty-board CTA): no widget or chat
      // to resume, just take me to the prompt.
      const isCreateMode = event?.detail?.mode === 'create';
      if (!targetWorkspaceId || (!widgetId && !agentChatId && !isCreateMode)) return;

      onOpenWidgetComposer({ workspaceId: targetWorkspaceId, widgetId, agentChatId });
      if (isBreakpointSm) {
        setMobileWidgetsOpen(true);
      } else {
        setLayoutPref('widgetsOpen', true);
      }
      if (targetWorkspaceId === activeWorkspaceId) return;

      const directMessage = await db.direct_messages.where('workspace_id').equals(targetWorkspaceId).first();
      if (directMessage?.id) {
        navigate(`/messages/${directMessage.id}`);
        return;
      }

      const workspace = await db.workspaces.get(targetWorkspaceId);
      navigate(workspaceRailPath(workspace || { id: targetWorkspaceId }));
    },
    [activeWorkspaceId, isBreakpointSm, navigate, onOpenWidgetComposer, setLayoutPref, setMobileWidgetsOpen],
  );

  useEffect(() => {
    window.addEventListener(OPEN_WIDGET_BUILDER_EVENT, handleOpenWidgetBuilder);
    return () => window.removeEventListener(OPEN_WIDGET_BUILDER_EVENT, handleOpenWidgetBuilder);
  }, [handleOpenWidgetBuilder]);
  const [widgetsEditing, setWidgetsEditing] = useState(false);
  const handleToggleWidgetsEditing = useCallback(function toggleWidgetsEditing() {
    setWidgetsEditing((editing) => !editing);
  }, []);
  // Reorder and Remove are two doors into the same room: edit mode gives drag-to-reorder
  // and the per-tile delete together, so both land there.
  const boardMenuAnchorRef = useRef(null);
  const [isBoardMenuOpen, setIsBoardMenuOpen] = useState(false);
  const handleToggleBoardMenu = useCallback(function toggleBoardMenu() {
    setIsBoardMenuOpen((open) => !open);
  }, []);
  const handleCloseBoardMenu = useCallback(function closeBoardMenu() {
    setIsBoardMenuOpen(false);
  }, []);

  useEffect(
    function closeBoardMenuWhenContextChanges() {
      setIsBoardMenuOpen(false);
    },
    [activeChannelId, activeDirectMessageId, activeWorkspaceId, boardWidgetCount],
  );

  // Every drop already saved, so edit mode holds no unsaved state — switching
  // chats or spaces just exits it silently rather than following the user to a
  // board it was never opened for.
  useEffect(() => {
    setWidgetsEditing(false);
  }, [activeWorkspaceId, activeChannelId, activeDirectMessageId]);

  const handleCloseWidgets = useCallback(
    function closeWidgets() {
      if (isBreakpointSm) {
        setMobileWidgetsOpen(false);
        return;
      }

      setLayoutPref('widgetsOpen', false);
    },
    [isBreakpointSm, setLayoutPref, setMobileWidgetsOpen],
  );
  const handleCloseMobileWidgets = useCallback(
    function closeMobileWidgets() {
      setMobileWidgetsOpen(false);
    },
    [setMobileWidgetsOpen],
  );
  const openWidgetGallery = useCallback(
    function openWidgetGallery(focusSlug) {
      setWidgetsPaneView(WIDGETS_PANE_VIEW.GALLERY);
      setGalleryFocusSlug(focusSlug || null);
      if (isBreakpointSm) {
        setMobileWidgetsOpen(true);
      } else {
        setLayoutPref('widgetsOpen', true);
      }
    },
    [isBreakpointSm, setLayoutPref, setMobileWidgetsOpen],
  );

  useEffect(
    function applyWidgetGalleryDeepLink() {
      if (requestedWidgetsPane !== WIDGETS_PANE_VIEW.GALLERY) {
        appliedWidgetGalleryDeepLinkRef.current = null;
        return;
      }
      if (!canShowWidgetGallery) return;

      const deepLinkKey = `${location}?${search}`;
      if (appliedWidgetGalleryDeepLinkRef.current === deepLinkKey) return;
      appliedWidgetGalleryDeepLinkRef.current = deepLinkKey;

      openWidgetGallery(requestedWidgetSlug);

      const nextSearchParams = new URLSearchParams(search);
      nextSearchParams.delete(WIDGETS_PANE_QUERY_PARAM);
      nextSearchParams.delete(WIDGET_QUERY_PARAM);
      const nextSearch = nextSearchParams.toString();
      navigate(nextSearch ? `${location}?${nextSearch}` : location, { replace: true });
    },
    [canShowWidgetGallery, location, navigate, openWidgetGallery, requestedWidgetSlug, requestedWidgetsPane, search],
  );

  const scrollWidgetIntoView = useCallback(function scrollWidgetIntoView(widgetId) {
    if (!widgetId) return;

    let attempt = 0;

    function scrollWhenReady() {
      const pane = activePaneBodyRef.current;
      if (!pane) return;

      const widgetElement = pane.querySelector(`[data-widget-id="${widgetIdSelectorValue(widgetId)}"]`);
      if (!widgetElement) {
        if (attempt < SCROLL_TO_WIDGET_MAX_ATTEMPTS) {
          attempt += 1;
          window.requestAnimationFrame(scrollWhenReady);
        }
        return;
      }

      const paneRect = pane.getBoundingClientRect();
      const widgetRect = widgetElement.getBoundingClientRect();
      pane.scrollTo({
        top: Math.max(0, pane.scrollTop + widgetRect.top - paneRect.top),
        behavior: 'smooth',
      });

      // Flash the card so the eye lands on the right widget after the jump.
      widgetElement.classList.remove('widget-jump-flash');
      // Force a reflow so re-adding the class restarts the animation on repeat jumps.
      void widgetElement.offsetWidth;
      widgetElement.classList.add('widget-jump-flash');
      widgetElement.addEventListener('animationend', () => widgetElement.classList.remove('widget-jump-flash'), { once: true });
    }

    scrollWhenReady();
  }, []);

  const handleOutputScrollToWidget = useCallback(
    function outputScrollToWidget(event) {
      const widgetId = event?.detail?.widgetId;
      if (!widgetId) return;

      markWidgetViewed(widgetId);
      setWidgetsPaneView(WIDGETS_PANE_VIEW.BOARD);
      if (isBreakpointSm) {
        setMobileWidgetsOpen(true);
      } else {
        setLayoutPref('widgetsOpen', true);
      }

      window.requestAnimationFrame(() => scrollWidgetIntoView(widgetId));
    },
    [isBreakpointSm, scrollWidgetIntoView, setLayoutPref, setMobileWidgetsOpen],
  );

  useEffect(() => {
    window.addEventListener(SCROLL_TO_WIDGET_EVENT, handleOutputScrollToWidget);
    return () => window.removeEventListener(SCROLL_TO_WIDGET_EVENT, handleOutputScrollToWidget);
  }, [handleOutputScrollToWidget]);

  useEffect(() => {
    if (previousLocationRef.current === location) return;
    previousLocationRef.current = location;
    if (!isBreakpointSm || !mobileWidgetsOpen) return;
    setMobileWidgetsOpen(false);
  }, [isBreakpointSm, location, mobileWidgetsOpen, setMobileWidgetsOpen]);

  useEffect(() => {
    if (isBreakpointSm || !mobileWidgetsOpen) return;
    setMobileWidgetsOpen(false);
  }, [isBreakpointSm, mobileWidgetsOpen, setMobileWidgetsOpen]);

  const widgetsPaneVisible = Boolean(activeWorkspaceId && widgetsPaneOpen);
  const mobileWidgetsVisible = Boolean(activeWorkspaceId && mobileWidgetsOpen);
  const widgetsPaneMarginRight = activeWorkspaceId && layoutSettings.widgetsOpen ? 0 : -WIDGETS_PANE_WIDTH;
  const widgetsPaneStyle = {
    '--widgets-pane-width': `${WIDGETS_PANE_WIDTH}px`,
    ...(isBreakpointSm && mobileWidgetsVisible ? { transform: `translateX(${mobileWidgetShelfDragX}px)` } : null),
    ...(isBreakpointSm ? null : { width: WIDGETS_PANE_WIDTH, marginRight: widgetsPaneMarginRight }),
  };

  return (
    <>
      <button
        type="button"
        aria-label="Close widget shelf"
        onClick={handleCloseMobileWidgets}
        className={cn(
          'absolute inset-0 z-(--stack-thread-list) bg-black/45 transition-opacity duration-300 ease-fluid sm:hidden',
          mobileWidgetsVisible ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
      />
      <section
        aria-hidden={!widgetsPaneVisible}
        inert={widgetsPaneVisible ? undefined : ''}
        style={widgetsPaneStyle}
        onPointerDown={handleWidgetShelfPointerDown}
        onPointerMove={handleWidgetShelfPointerMove}
        onPointerUp={handleWidgetShelfPointerUp}
        onPointerCancel={handleWidgetShelfPointerCancel}
        className={cn(
          `absolute top-0 right-0 bottom-0 z-(--stack-agent-chat-content-overlay) flex w-[min(100%,var(--widgets-pane-width))] touch-pan-y flex-col rounded-tr-lg bg-transparent shadow-lg max-sm:overflow-hidden sm:relative sm:top-auto sm:right-auto sm:bottom-auto sm:z-auto sm:h-full sm:flex-none sm:shadow-none ${roundedClasses[1]}`,
          hasWorkspaceBackgroundImage && WORKSPACE_BACKGROUND_IMAGE_CLASS_NAME,
          mobileWidgetsVisible ? '' : 'max-sm:pointer-events-none max-sm:translate-x-full',
          isBreakpointSm
            ? mobileWidgetShelfDragging
              ? 'transition-none'
              : 'transition-transform duration-300 ease-fluid'
            : 'transition-sizes duration-300 ease-fluid',
        )}
      >
        {/* Mobile only: the shelf slides over the chat, so it needs its own opaque
            backdrop. On sm+ the pane sits beside the chat and sees through to the
            spanning background image painted by the layout wrapper. */}
        <div aria-hidden className="absolute inset-0 sm:hidden">
          <WorkspaceBackgroundImageLayer style={workspaceBackgroundImageStyle} />
        </div>
        {/* gap-2, tighter than the shared header row's gap-3: three pills in a row read as
            one control group at 8px and as three unrelated buttons at 12px. */}
        <LayoutNavBar withBackdrop backdropClassName="sm:hidden" className="z-20 justify-end gap-2">
          {widgetsEditing ? (
            <EditWidgetsButton onClick={handleToggleWidgetsEditing} active onImage={hasWorkspaceBackgroundImage} />
          ) : (
            <>
              <WidgetsNewPill onCreate={handleOpenWidgetComposer} onImage={hasWorkspaceBackgroundImage} />
              {/* An empty board has nothing to reorder or remove, so the overflow stays
                  away while New and the empty-state CTA remain available. */}
              {boardWidgetCount > 0 ? (
                <div ref={boardMenuAnchorRef} className="flex shrink-0">
                  <WidgetsBoardMenuButton open={isBoardMenuOpen} onToggle={handleToggleBoardMenu} onImage={hasWorkspaceBackgroundImage} />
                </div>
              ) : null}
              <CloseWidgetsButton onClick={handleCloseWidgets} onImage={hasWorkspaceBackgroundImage} />
              <WidgetsBoardMenu
                anchorRef={boardMenuAnchorRef}
                open={isBoardMenuOpen}
                onClose={handleCloseBoardMenu}
                onReorder={handleToggleWidgetsEditing}
                onRemove={handleToggleWidgetsEditing}
                onBrowse={handleBrowseWidgetLibrary}
              />
            </>
          )}
        </LayoutNavBar>
        <div className="relative isolate z-0 min-h-0 flex-1 bg-transparent">
          {mountedWorkspaceIds.map((retainedWorkspaceId) => (
            <SurfaceErrorBoundary key={retainedWorkspaceId} surface="widgets-pane">
              <RetainedWorkspacePaneEntry
                workspaceId={retainedWorkspaceId}
                directMessageId={
                  retainedWorkspaceId === activeWorkspaceId ? activeDirectMessageId : directMessageIdsByWorkspace[retainedWorkspaceId]
                }
                isActive={retainedWorkspaceId === activeWorkspaceId}
                activePaneBodyRef={activePaneBodyRef}
                activeChannelId={retainedWorkspaceId === activeWorkspaceId ? activeChannelId : null}
                galleryFocusSlug={galleryFocusSlug}
                widgetsPaneView={widgetsPaneView}
                widgetsEditing={widgetsEditing}
              />
            </SurfaceErrorBoundary>
          ))}
        </div>
      </section>
    </>
  );
}

// Search, Channels, and Video share a single pill that carries the exact chrome of the
// other header buttons. The buttons inside render `surfaceless` (no fill, shadow, or
// backdrop-filter of their own — even a no-op child backdrop-filter punches a hole in
// the pill's glass) and raise their circle only on hover.
const navGroupItemClassName = (onImage) =>
  cn(
    'size-8 border-0 bg-transparent shadow-none dark:bg-transparent',
    // Mirrors the grid button's hover, which lightens by dropping black/60 → black/40 —
    // a gain of 20% of the (mostly dark) backdrop, not 20% of pure white. Inside the
    // pill only a white overlay can lighten, so it runs at half strength to match the
    // grid's perceived step over dark imagery.
    onImage ? 'hover:bg-white/10' : 'hover:bg-white dark:hover:bg-neutral-800',
  );

const AvNavButton = React.memo(function AvNavButton({ channelId, navigate, onImage = false, className }) {
  const { workspaceId, profileId } = useRouteWorkspace();
  const { status, credentials, joinConference } = useConference();
  const activeConferenceChannelId = credentials?.channelId ? String(credentials.channelId) : null;
  const targetChannelId = channelId ? String(channelId) : null;
  const sharedConferenceTranscript = useSingleChannelLatestConferenceTranscript(targetChannelId);
  const hasOngoingConference =
    sharedConferenceTranscript !== DEXIE_IS_LOADING &&
    !!sharedConferenceTranscript?.data?.started_at &&
    !sharedConferenceTranscript?.data?.ended_at;
  const isJoinedToTarget = !!targetChannelId && ACTIVE_CONFERENCE_STATUSES.has(status) && activeConferenceChannelId === targetChannelId;
  const isActive = !!targetChannelId && (isJoinedToTarget || hasOngoingConference);

  const handleClick = useCallback(() => {
    if (!targetChannelId) return;

    if (isJoinedToTarget) {
      navigate(`/chats/${targetChannelId}`);
      return;
    }

    if (ACTIVE_CONFERENCE_STATUSES.has(status)) {
      if (activeConferenceChannelId) navigate(`/chats/${activeConferenceChannelId}`);
      return;
    }

    joinConference(targetChannelId, { workspaceId, profileId }).catch(() => {
      // ConferenceContext handles logging and error state.
    });
  }, [activeConferenceChannelId, isJoinedToTarget, joinConference, navigate, profileId, status, targetChannelId, workspaceId]);

  return (
    <Tooltip label="Video chat" className="flex shrink-0">
      <NavButton onClick={handleClick} active={isActive} aria-label="Video chat" onImage={onImage} surfaceless className={className}>
        <Video className={cn('size-4.5', onImage ? 'text-white' : 'text-black')} strokeWidth={2} absoluteStrokeWidth />
      </NavButton>
    </Tooltip>
  );
});

const MainLayoutContent = () => {
  const {
    workspaceId,
    profile: currentProfile,
    workspace: currentWorkspace,
    mainChannelId,
    entitySnapshots,
    isDirectMessage = false,
  } = useRouteWorkspace();
  const workspaceRailWidth = useContext(WorkspaceRailWidthContext);
  const { isChatRoute, mobileWidgetsActive, mobileWidgetsOpen, setMobileWidgetsOpen, toggleWidgets, widgetsPaneOpen } =
    useContext(WidgetsPaneControlsContext);
  const [location, navigate] = useLocation();
  const search = useSearch();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const showWorkspaceManagement = !isDirectMessage;
  const showDirectMessagePreferences = isDirectMessage;
  // App shortcuts are desktop-only; browser builds should keep browser/OS chords like Cmd+F.
  const appShortcutsEnabled = getMetaContent('app:variant') === NATIVE_MACOS_APP_VARIANT;
  const activeModal = searchParams.get('modal');
  const isInvitePeopleModalOpen = showWorkspaceManagement && activeModal === 'invite-people';
  const isMembersModalOpen = showWorkspaceManagement && activeModal === 'members';
  const isWorkspaceOptionsModalOpen = showWorkspaceManagement && activeModal === 'workspace-options';
  const isDirectMessagePreferencesModalOpen = showDirectMessagePreferences && activeModal === 'direct-message-preferences';
  // Which body the shared workspace-management portal shows; retained so content survives the close fade.
  const activeWorkspaceManagementModal = isInvitePeopleModalOpen
    ? 'invite-people'
    : isWorkspaceOptionsModalOpen
      ? 'workspace-options'
      : null;
  const retainedWorkspaceManagementModal = useRetainedModalValue(activeWorkspaceManagementModal);
  const [inviteOpenedFromSettings, setInviteOpenedFromSettings] = useState(false);
  // Set when the invite sheet is opened from a suggested face on the onboarding card.
  const [onboardingInviteUser, setOnboardingInviteUser] = useState(null);
  const requestedWorkspaceOptionsSection = searchParams.get(WORKSPACE_OPTIONS_SECTION_QUERY_PARAM);
  const workspaceOptionsDefaultSection = WORKSPACE_OPTIONS_DEEP_LINK_SECTIONS.has(requestedWorkspaceOptionsSection)
    ? requestedWorkspaceOptionsSection
    : WORKSPACE_OPTIONS_SECTION.ROOT;
  const [, channelRouteParams] = useRoute('/chats/:channelId');
  const [isAgentIndexRoute] = useRoute('/agent');
  const [isAgentDetailRoute, agentRouteParams] = useRoute('/agent/:agentChatId');
  const [isSearchRoute] = useRoute('/search');
  // Search is an inline header overlay, not a route: the nav pill swaps to an input and
  // results float over the still-mounted chat. The /search route only survives as a
  // deep-link that opens the overlay (see the redirect effect by handleShowSearch).
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const isAgentRoute = isAgentIndexRoute || isAgentDetailRoute;
  const { hasWorkspaceBackgroundImage } = useWorkspaceBackgroundImage(currentWorkspace, isChatRoute);
  const isReadingChatSurface =
    isChatRoute && !isInvitePeopleModalOpen && !isMembersModalOpen && !isWorkspaceOptionsModalOpen && !isDirectMessagePreferencesModalOpen;
  const activeChannelIdRef = useRef(channelRouteParams?.channelId || null);
  activeChannelIdRef.current = channelRouteParams?.channelId || null;

  const isBreakpointSm = useMediaQuery(BREAKPOINT_SM);
  // Video chat UI strictly follows the Beta Features toggle, like channels.
  const canUseAvCalling = isAvCallingEnabled(currentWorkspace);

  // conversation the user is reading: while window+tab focused, omit from document title / Electron totals; include again when backgrounded.
  const unreadActiveChannelId = useMemo(() => {
    if (isReadingChatSurface) return channelRouteParams?.channelId || mainChannelId || null;
    return null;
  }, [isReadingChatSurface, channelRouteParams?.channelId, mainChannelId]);

  const unreadReportChannelIds = useUnreadReportChannelIds();
  const sharedAvChannelId = mainChannelId;
  const activeRouteChannelId = channelRouteParams?.channelId || null;
  const routeAgentChatId = isAgentDetailRoute ? agentRouteParams?.agentChatId || null : null;

  useEffect(() => {
    if (getMetaContent('app:unsupported') !== 'true') return;
    window.dispatchEvent(new CustomEvent('output:internal:nagbar', { detail: { reason: 'unsupported' } }));
  }, []);

  const { settings: layoutSettings, setLayoutPref } = useLayoutPreferences();

  const threadListChannels = useThreadListChannels();
  const activeThreadChannelId = isChatRoute ? activeRouteChannelId || mainChannelId || null : null;
  const unreadThreadListChannelIds = useMemo(
    function buildUnreadThreadListChannelIds() {
      return threadListChannels
        .map((channel) => channel?.id)
        .filter((channelId) => channelId != null && String(channelId) !== String(activeThreadChannelId));
    },
    [activeThreadChannelId, threadListChannels],
  );
  const threadListUnreadSignal = useChannelUnreadSignal(unreadThreadListChannelIds);
  const hasThreadListUnread = threadListUnreadSignal.messages > 0 || threadListUnreadSignal.mentions > 0;
  // Channels UI strictly follows the Beta Features toggle: turning it off hides the
  // nav and thread list even when channels exist (they're hidden, not deleted).
  const channelsEnabled = Boolean(currentWorkspace?.channels_enabled);
  const showChannelsNav = channelsEnabled;
  // Search is always present; Channels and Video are conditional. With company the trio
  // shares a pill; alone, Search renders as a standalone button like the grid button.
  const navGroupIsPill = showChannelsNav || canUseAvCalling;
  // When reading a breakout, its name hangs in a small pill 8px under the chat title.
  const activeBreakoutChannel = useMemo(
    () =>
      threadListChannels.find(
        (channel) =>
          channel?.kind === 'open_breakout' && activeRouteChannelId != null && String(channel.id) === String(activeRouteChannelId),
      ) || null,
    [activeRouteChannelId, threadListChannels],
  );
  const handleEditActiveBreakout = useCallback(
    function handleEditActiveBreakoutCallback() {
      if (!activeBreakoutChannel) return;

      // Same event EndOfMessages and the thread list use; MainLayout's listener opens ChannelSettings.
      window.dispatchEvent(
        new CustomEvent('output:internal:channel-settings-modal', {
          detail: { channelId: activeBreakoutChannel.id },
        }),
      );
    },
    [activeBreakoutChannel],
  );
  const handleToggleThreadList = useCallback(
    function handleToggleThreadListCallback() {
      setLayoutPref('threadListVisible', (v) => !v);
    },
    [setLayoutPref],
  );
  const [channelSettingsModalData, setChannelSettingsModalData] = useState(null);
  const [addEmojiModalOpen, setAddEmojiModalOpen] = useState(false);
  const handleCloseChannelSettingsModal = useCallback(function closeChannelSettingsModal() {
    setChannelSettingsModalData(null);
  }, []);
  const [isNewChannelModalOpen, setIsNewChannelModalOpen] = useState(false);
  const handleOpenNewChannelModal = useCallback(function openNewChannelModal() {
    setIsNewChannelModalOpen(true);
  }, []);
  const handleCloseNewChannelModal = useCallback(function closeNewChannelModal() {
    setIsNewChannelModalOpen(false);
  }, []);
  const handleChannelCreated = useCallback(
    // Seed Dexie from the create response before navigating. Without it the new channel
    // reads as unavailable until the journal catches up, and ChatView's unavailable
    // handler bounces you straight back to the parent chat.
    async function handleChannelCreatedCallback(channel) {
      setIsNewChannelModalOpen(false);
      if (!channel?.id) return;
      try {
        await db.channels.put(channel);
      } catch (error) {
        logError('MainLayout', 'Could not seed created channel', { error });
      }
      navigate(`/chats/${channel.id}`);
    },
    [navigate],
  );
  const retainedChannelSettingsModalData = useRetainedModalValue(channelSettingsModalData);

  const sharedAvIsInteractive = true; // main channel is always interactive

  // kick to root if the workspace is deactivated
  useEffect(() => {
    if (!currentWorkspace?.slug?.startsWith('__deactivated__')) return;
    window.dispatchEvent(new CustomEvent('output:kill-workspace', { detail: { workspaceId: currentWorkspace.id } }));
  }, [currentWorkspace?.id, currentWorkspace?.slug]);

  const [playSoundConferenceStarted] = useSound('/sounds/conference-started.mp3');
  const [playSoundConferenceParticipantJoined] = useSound('/sounds/conference-participant-joined.mp3');
  const [playSoundConferenceParticipantLeft] = useSound('/sounds/conference-participant-left.mp3');
  const [playSoundConferenceEnded] = useSound('/sounds/conference-ended.mp3');
  const conferenceSfxDisabled = useMemo(
    () => currentProfile?.preferences?.conference_sfx_disabled || false,
    [currentProfile?.preferences?.conference_sfx_disabled],
  );

  const roundedClasses = useMainPanelRoundedClasses();

  // Keep an explicit same-route fallback for mobile widgets: tapping the current nav item
  // does not change Wouter location, so the route-change effect below would never close the shelf.
  const navigateOrShowMain = useCallback(
    function navigateOrShowMain(nextPath, isCurrentRoute) {
      if (isBreakpointSm && mobileWidgetsOpen) {
        setMobileWidgetsOpen(false);
        if (isCurrentRoute) return;
      }

      navigate(nextPath);
    },
    [isBreakpointSm, mobileWidgetsOpen, navigate, setMobileWidgetsOpen],
  );
  const mainChatPath = mainChannelId ? `/chats/${mainChannelId}` : '/chats';
  const lastChannelPathRef = useRef(mainChatPath);

  useEffect(
    function rememberLastChatPath() {
      if (!isChatRoute) return;
      lastChannelPathRef.current = location || mainChatPath;
    },
    [isChatRoute, location, mainChatPath],
  );

  const navigateWithModalParam = useCallback(
    function navigateWithModalParam(modal, options = {}) {
      const { workspaceOptionsSection, ...navigateOptions } = options;
      const nextSearchParams = new URLSearchParams(search);
      if (modal) {
        nextSearchParams.set('modal', modal);
      } else {
        nextSearchParams.delete('modal');
      }

      if (modal === 'workspace-options' && workspaceOptionsSection) {
        nextSearchParams.set(WORKSPACE_OPTIONS_SECTION_QUERY_PARAM, workspaceOptionsSection);
      } else {
        nextSearchParams.delete(WORKSPACE_OPTIONS_SECTION_QUERY_PARAM);
      }

      const nextSearch = nextSearchParams.toString();
      navigate(nextSearch ? `${location}?${nextSearch}` : location, navigateOptions);
    },
    [location, navigate, search],
  );

  const handleCloseMembers = useCallback(
    function closeMembers() {
      navigateWithModalParam(null, { replace: true });
    },
    [navigateWithModalParam],
  );

  const handleCloseInvitePeople = useCallback(
    function closeInvitePeople() {
      navigateWithModalParam(null, { replace: true });
    },
    [navigateWithModalParam],
  );

  const handleCloseWorkspaceOptionsModal = useCallback(
    function closeWorkspaceOptionsModal() {
      navigateWithModalParam(null, { replace: true });
    },
    [navigateWithModalParam],
  );

  const handleCloseDirectMessagePreferencesModal = useCallback(
    function closeDirectMessagePreferencesModal() {
      navigateWithModalParam(null, { replace: true });
    },
    [navigateWithModalParam],
  );

  const handleShowWorkspaceOptions = useCallback(
    function showWorkspaceOptions() {
      if (isBreakpointSm && mobileWidgetsOpen) {
        setMobileWidgetsOpen(false);
      }

      if (!isWorkspaceOptionsModalOpen) {
        navigateWithModalParam('workspace-options');
      }
    },
    [isBreakpointSm, isWorkspaceOptionsModalOpen, mobileWidgetsOpen, navigateWithModalParam, setMobileWidgetsOpen],
  );

  const handleShowDirectMessagePreferences = useCallback(
    function showDirectMessagePreferences() {
      if (isBreakpointSm && mobileWidgetsOpen) {
        setMobileWidgetsOpen(false);
      }

      if (!isDirectMessagePreferencesModalOpen) {
        navigateWithModalParam('direct-message-preferences');
      }
    },
    [isBreakpointSm, isDirectMessagePreferencesModalOpen, mobileWidgetsOpen, navigateWithModalParam, setMobileWidgetsOpen],
  );

  const handleShowWorkspaceSettings = useCallback(
    function showWorkspaceSettings() {
      if (showWorkspaceManagement) {
        handleShowWorkspaceOptions();
        return;
      }

      if (showDirectMessagePreferences) {
        handleShowDirectMessagePreferences();
      }
    },
    [handleShowDirectMessagePreferences, handleShowWorkspaceOptions, showDirectMessagePreferences, showWorkspaceManagement],
  );

  const handleShowMembersFromWorkspaceOptions = useCallback(
    function showMembersFromWorkspaceOptions() {
      navigateWithModalParam('members');
    },
    [navigateWithModalParam],
  );

  const handleShowInvitePeople = useCallback(
    function showInvitePeople() {
      if (isBreakpointSm && mobileWidgetsOpen) {
        setMobileWidgetsOpen(false);
      }

      if (!isInvitePeopleModalOpen) {
        // Opened from the settings modal → its chrome button becomes a back chevron;
        // opened directly (e.g. chat-top Invite button) → it stays an X that closes.
        setInviteOpenedFromSettings(isWorkspaceOptionsModalOpen);
        navigateWithModalParam('invite-people');
      }
    },
    [isBreakpointSm, isInvitePeopleModalOpen, isWorkspaceOptionsModalOpen, mobileWidgetsOpen, navigateWithModalParam, setMobileWidgetsOpen],
  );

  const handleOpenWidgetsFromOnboarding = useCallback(
    function openWidgetsFromOnboarding() {
      if (!widgetsPaneOpen) toggleWidgets();
    },
    [toggleWidgets, widgetsPaneOpen],
  );

  const handleInviteFromOnboarding = useCallback(
    function inviteFromOnboarding(event) {
      // Tapping a suggested face carries that person into the sheet already selected.
      setOnboardingInviteUser(event?.detail?.user || null);
      handleShowInvitePeople();
    },
    [handleShowInvitePeople],
  );

  // The empty-state onboarding cards sit deep inside the message feed, so they ask for
  // the invite modal and the widgets pane by event rather than threading callbacks down.
  useEffect(
    function listenForOnboardingCardEvents() {
      window.addEventListener(ONBOARDING_INVITE_PEOPLE_EVENT, handleInviteFromOnboarding);
      window.addEventListener(ONBOARDING_OPEN_WIDGETS_EVENT, handleOpenWidgetsFromOnboarding);

      return function removeOnboardingCardEventListeners() {
        window.removeEventListener(ONBOARDING_INVITE_PEOPLE_EVENT, handleInviteFromOnboarding);
        window.removeEventListener(ONBOARDING_OPEN_WIDGETS_EVENT, handleOpenWidgetsFromOnboarding);
      };
    },
    [handleInviteFromOnboarding, handleOpenWidgetsFromOnboarding],
  );

  const handleShowMembers = useCallback(
    function showMembers() {
      if (isBreakpointSm && mobileWidgetsOpen) {
        setMobileWidgetsOpen(false);
      }

      if (!isMembersModalOpen) {
        navigateWithModalParam('members');
      }
    },
    [isBreakpointSm, isMembersModalOpen, mobileWidgetsOpen, navigateWithModalParam, setMobileWidgetsOpen],
  );

  const handleShowSearch = useCallback(function showSearch() {
    setIsSearchOpen((open) => !open);
  }, []);

  const handleOpenSearchFromSettingsModal = useCallback(
    function openSearchFromSettingsModal() {
      navigateWithModalParam(null, { replace: true });
      setIsSearchOpen(true);
    },
    [navigateWithModalParam],
  );

  // Legacy /search deep links open the overlay and settle on the last chat URL.
  useEffect(
    function redirectSearchRouteToOverlay() {
      if (!isSearchRoute) return;
      setIsSearchOpen(true);
      navigateOrShowMain(lastChannelPathRef.current || mainChatPath, false);
    },
    [isSearchRoute, mainChatPath, navigateOrShowMain],
  );

  const handleGlobalDocumentClick = useCallback(
    (event) => {
      const anchor = event.target.closest('a');
      if (!anchor || !anchor.href) return;
      if (anchor.closest('[data-workspace-rail]')) return;

      let parsed;
      try {
        parsed = parseWorkspacePath(new URL(anchor.href, window.location.origin).pathname);
      } catch {
        return;
      }
      if (!parsed || parsed.workspaceId !== workspaceId) return;

      const channelMatch = parsed.relativePath.match(/^\/chats\/([^/]+)/);
      if (!channelMatch) return;

      window.dispatchEvent(new CustomEvent('output:activate-channel', { detail: { channelId: channelMatch[1] } }));
      event.preventDefault();
      event.stopPropagation();
    },
    [workspaceId],
  );

  const handleOutputActivateChannel = useCallback(
    async (event) => {
      const { channelId } = event.detail;

      if (channelId && workspaceId) {
        const channel = await db.channels.get([workspaceId, String(channelId)]);
        if (channel) {
          navigate(`/chats/${channelId}`);
          return;
        }
      }

      // If Dexie has not hydrated the workspace record yet, `mainChannelId` can be
      // transiently null during a workspace switch or after an unavailable channel
      // redirect. In that window, send the user to `/chats` and let the react app
      // resolve the active channel once the workspace data is ready.
      navigate(mainChatPath);
    },
    [mainChatPath, navigate, workspaceId],
  );

  const handleOutputActivateAgentChat = useCallback(
    async function activateAgentChat(event) {
      const { agentChatId: newAgentChatId } = event.detail;
      logDebug('MainLayout', 'Received `output:activate-agent-chat` event', { agentChatId: newAgentChatId });

      if (newAgentChatId) {
        navigate(`/agent/${newAgentChatId}`);
        return;
      }

      navigate('/agent');
    },
    [navigate],
  );

  const handleOutputOpenChannelSettingsModal = useCallback((event) => {
    setChannelSettingsModalData(event.detail);
  }, []);

  const handleOutputOpenAddEmojiModal = useCallback(() => {
    setAddEmojiModalOpen(true);
  }, []);

  // While the emoji upload is in flight, the accidental dismissal paths (esc, outside
  // press) funnel through here and get ignored so the create can't be orphaned.
  const addEmojiSubmittingRef = useRef(false);
  const handleAddEmojiSubmittingChange = useCallback((isSubmitting) => {
    addEmojiSubmittingRef.current = isSubmitting;
  }, []);

  const handleCloseAddEmojiModal = useCallback((options) => {
    // `force` is the deliberate exit — the X passes it, esc and outside-press don't.
    // Without it, an upload that wedges without ever reporting completed or error would
    // leave the gate armed forever and trap the user in a modal with no working close.
    // The modal sweeps its own attachment on unmount, so bailing early costs nothing.
    if (addEmojiSubmittingRef.current && !options?.force) return;
    addEmojiSubmittingRef.current = false;
    setAddEmojiModalOpen(false);
    // MainLayoutFrame resumes the chat-wide dropzone on this signal.
    window.dispatchEvent(new CustomEvent('output:internal:add-emoji-modal-closed'));
  }, []);

  const handleOutputInternalSoundEffect = useCallback(
    (event) => {
      const { clip } = event.detail;
      logDebug('MainLayout', 'Received `output:internal:sfx:one-shot` event', { clip, conferenceSfxDisabled });
      if (conferenceSfxDisabled) return;

      switch (clip) {
        case 'conference-started':
          playSoundConferenceStarted();
          break;
        case 'conference-participant-joined':
          playSoundConferenceParticipantJoined();
          break;
        case 'conference-participant-left':
          playSoundConferenceParticipantLeft();
          break;
        case 'conference-ended':
          playSoundConferenceEnded();
          break;
        default:
          break;
      }
    },
    [
      conferenceSfxDisabled,
      playSoundConferenceEnded,
      playSoundConferenceParticipantJoined,
      playSoundConferenceParticipantLeft,
      playSoundConferenceStarted,
    ],
  );

  const mainRouteContent = (
    <>
      {isChatRoute && <ChatView channelId={activeRouteChannelId || sharedAvChannelId} />}
      {isAgentRoute && <AgentChatPageView agentChatId={routeAgentChatId} />}
    </>
  );

  useEffect(() => {
    document.addEventListener('click', handleGlobalDocumentClick);
    return () => {
      document.removeEventListener('click', handleGlobalDocumentClick);
    };
  }, [handleGlobalDocumentClick]);

  useEffect(() => {
    window.addEventListener('output:internal:sfx:one-shot', handleOutputInternalSoundEffect);

    return () => {
      window.removeEventListener('output:internal:sfx:one-shot', handleOutputInternalSoundEffect);
    };
  }, [handleOutputInternalSoundEffect]);

  useEffect(() => {
    window.addEventListener('output:activate-channel', handleOutputActivateChannel);
    window.addEventListener('output:activate-agent-chat', handleOutputActivateAgentChat);
    window.addEventListener('output:internal:channel-settings-modal', handleOutputOpenChannelSettingsModal);
    window.addEventListener('output:internal:add-emoji-modal', handleOutputOpenAddEmojiModal);
    window.addEventListener('output:internal:invite-people-modal', handleShowInvitePeople);
    window.addEventListener('output:internal:members-modal', handleShowMembers);

    return () => {
      window.removeEventListener('output:activate-channel', handleOutputActivateChannel);
      window.removeEventListener('output:activate-agent-chat', handleOutputActivateAgentChat);
      window.removeEventListener('output:internal:channel-settings-modal', handleOutputOpenChannelSettingsModal);
      window.removeEventListener('output:internal:add-emoji-modal', handleOutputOpenAddEmojiModal);
      window.removeEventListener('output:internal:invite-people-modal', handleShowInvitePeople);
      window.removeEventListener('output:internal:members-modal', handleShowMembers);
    };
  }, [
    handleOutputActivateAgentChat,
    handleOutputActivateChannel,
    handleOutputOpenAddEmojiModal,
    handleOutputOpenChannelSettingsModal,
    handleShowInvitePeople,
    handleShowMembers,
  ]);

  return (
    <EntitySnapshotContext.Provider value={entitySnapshots}>
      <WorkspacePresenceManager viewingChannelId={unreadActiveChannelId} />
      <UnreadManager activeChannelId={unreadActiveChannelId} unreadReportChannelIds={unreadReportChannelIds} />
      {appShortcutsEnabled ? <WorkspaceSettingsShortcutRouter onOpenSettings={handleShowWorkspaceSettings} /> : null}
      {appShortcutsEnabled ? <WorkspaceSearchShortcutRouter onToggleSearch={handleShowSearch} /> : null}
      <SearchEscapeRouter active={isSearchOpen} onCloseSearch={handleShowSearch} />
      <ConferenceBubble
        currentChannelId={sharedAvChannelId}
        isInteractive={sharedAvIsInteractive}
        sidebarWidthOverride={workspaceRailWidth}
        toolbarTopClassName="top-18"
        hidePreJoinUi
      />
      <div
        className={cn('relative flex min-h-0 flex-1 overflow-hidden', hasWorkspaceBackgroundImage ? 'bg-transparent' : 'bg-gray-100')}
        data-app-region="none"
        data-floating-boundary
      >
        <div className={cn('relative flex min-w-0 flex-1 flex-col', hasWorkspaceBackgroundImage && WORKSPACE_BACKGROUND_IMAGE_CLASS_NAME)}>
          <LayoutNavBar className="grid grid-cols-[1fr_auto_1fr] items-center">
            {/* Search alone renders as a standalone button (same chrome as the grid
                  button); two or more icons share the pill with surfaceless items.
                  While searching, the whole cluster swaps for the inline input pill. */}
            {isSearchOpen ? (
              <HeaderSearch onImage={hasWorkspaceBackgroundImage} onRequestClose={handleShowSearch} />
            ) : (
              <div className="flex items-start gap-2 justify-self-start">
                <div
                  className={cn(
                    // On mobile the pill stacks vertically (a floating column hanging from
                    // the bar); sm+ lays it out as the horizontal pill.
                    'flex shrink-0 items-center gap-0.5 max-sm:flex-col max-sm:self-start',
                    navGroupIsPill ? 'max-sm:w-9 max-sm:py-0.5 sm:h-9 sm:px-0.5' : 'h-9',
                    navGroupIsPill &&
                      'rounded-full border-[0.5px] border-white bg-gray-50 shadow-[0_4px_16px_0_rgba(0,0,0,0.1)] dark:border-white/10',
                    navGroupIsPill &&
                      hasWorkspaceBackgroundImage &&
                      'border-0 bg-black/60 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.2),0_4px_24px_0_rgba(0,0,0,0.5)] backdrop-blur-[15px] backdrop-saturate-200',
                  )}
                >
                  <Tooltip label="Search" className="flex shrink-0">
                    <NavButton
                      onClick={handleShowSearch}
                      active={isSearchOpen}
                      aria-label="Search"
                      onImage={hasWorkspaceBackgroundImage}
                      surfaceless={navGroupIsPill}
                      className={navGroupIsPill ? navGroupItemClassName(hasWorkspaceBackgroundImage) : undefined}
                    >
                      <Search
                        className={cn('size-4.5', hasWorkspaceBackgroundImage ? 'text-white' : 'text-black')}
                        strokeWidth={2}
                        absoluteStrokeWidth
                      />
                    </NavButton>
                  </Tooltip>
                  {showChannelsNav ? (
                    <Tooltip label="Channels" className="flex shrink-0">
                      <NavButton
                        onClick={handleToggleThreadList}
                        active={layoutSettings.threadListVisible}
                        aria-label={hasThreadListUnread ? 'Channels, unread' : 'Channels'}
                        showUnreadDot={hasThreadListUnread}
                        unreadDotClassName="top-[6px] right-[6px] size-1.5 bg-idle-blue"
                        onImage={hasWorkspaceBackgroundImage}
                        surfaceless
                        className={navGroupItemClassName(hasWorkspaceBackgroundImage)}
                      >
                        <Hash
                          className={cn('size-4.5', hasWorkspaceBackgroundImage ? 'text-white' : 'text-black')}
                          strokeWidth={2}
                          absoluteStrokeWidth
                        />
                      </NavButton>
                    </Tooltip>
                  ) : null}
                  {canUseAvCalling ? (
                    <div className="hidden sm:flex">
                      <AvNavButton
                        channelId={sharedAvChannelId}
                        navigate={navigate}
                        onImage={hasWorkspaceBackgroundImage}
                        className={navGroupItemClassName(hasWorkspaceBackgroundImage)}
                      />
                    </div>
                  ) : null}
                </div>
                {showWorkspaceManagement ? (
                  <InvitePeopleButton onClick={handleShowInvitePeople} onImage={hasWorkspaceBackgroundImage} />
                ) : null}
              </div>
            )}
            <div className="relative flex min-w-0 justify-center justify-self-center">
              <WorkspaceHeaderButton
                onClick={handleShowWorkspaceSettings}
                active={
                  (isInvitePeopleModalOpen || isWorkspaceOptionsModalOpen || isDirectMessagePreferencesModalOpen) && !mobileWidgetsActive
                }
                title={showWorkspaceManagement ? 'Workspace options' : 'Preferences'}
                resource={currentWorkspace}
                onImage={hasWorkspaceBackgroundImage}
              >
                {currentWorkspace?.title || 'Workspace'}
              </WorkspaceHeaderButton>
              {activeBreakoutChannel ? (
                <button
                  type="button"
                  onClick={handleEditActiveBreakout}
                  aria-label="Edit breakout"
                  data-app-region="none"
                  className={cn(
                    // Layout-centered (inset-x-0 + mx-auto + w-fit) rather than translate(-50%):
                    // layout positions pixel-snap at paint, transforms don't — an odd pill width
                    // would land the composited layer on a half pixel and blur the text.
                    'absolute inset-x-0 top-[calc(100%+8px)] mx-auto flex w-fit max-w-[min(52vw,360px)] cursor-pointer items-center justify-center rounded-full px-2.5 py-0.5',
                    hasWorkspaceBackgroundImage
                      ? 'bg-black/60 text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.2),0_4px_24px_0_rgba(0,0,0,0.5)] backdrop-blur-[15px] backdrop-saturate-200 hover:bg-black/40'
                      : 'border-[0.5px] border-white bg-gray-50 text-black shadow-[0_4px_16px_0_rgba(0,0,0,0.1)] backdrop-blur-md hover:bg-white dark:border-white/10 dark:bg-neutral-900 dark:text-white dark:hover:bg-neutral-800',
                  )}
                >
                  <span className="truncate text-[13px] leading-[22px] font-medium">{`# ${getChannelDisplayName(activeBreakoutChannel)}`}</span>
                </button>
              ) : null}
            </div>
            <div
              className={cn(
                'flex shrink-0 items-center gap-2 justify-self-end transition-transform duration-300 ease-fluid',
                widgetsPaneOpen ? 'translate-x-11' : 'translate-x-0',
              )}
            >
              <Tooltip label="Show widgets" className={cn('flex shrink-0', widgetsPaneOpen && 'pointer-events-none')}>
                <NavButton
                  onClick={toggleWidgets}
                  active={widgetsPaneOpen}
                  aria-label="Show widgets"
                  onImage={hasWorkspaceBackgroundImage}
                  aria-hidden={widgetsPaneOpen}
                  tabIndex={widgetsPaneOpen ? -1 : undefined}
                  className={cn(
                    'transition-opacity duration-150 ease-fluid',
                    widgetsPaneOpen ? 'pointer-events-none opacity-0 delay-75' : 'opacity-100 delay-0',
                  )}
                >
                  <Grid2x2
                    className={cn('size-4.5', hasWorkspaceBackgroundImage ? 'text-white' : 'text-black')}
                    strokeWidth={2}
                    absoluteStrokeWidth
                  />
                </NavButton>
              </Tooltip>
            </div>
          </LayoutNavBar>
          <div
            className={cn(
              // `isolate` keeps the chat's sticky header/jump-to-newest z-stack below the floating nav bar.
              'layout-main-panel isolate flex min-h-0 flex-1 flex-col overflow-hidden',
              hasWorkspaceBackgroundImage ? 'bg-transparent' : 'rounded-tl-lg bg-gray-50',
              !hasWorkspaceBackgroundImage && (isBreakpointSm || !widgetsPaneOpen) && `rounded-tr-lg ${roundedClasses[1]}`,
            )}
          >
            <div className="flex min-h-0 flex-1">
              <div
                // The offset parent for anything positioned against the chat column —
                // ThreadList's `top-[72px]` is measured from here, and the call window reads
                // this box so it can line up with the channels card from outside the tree.
                data-chat-column
                className={cn(
                  'relative min-h-0 min-w-0 flex-1',
                  isAgentRoute && 'flex min-h-0 flex-1 flex-col overflow-hidden',
                  isChatRoute || isAgentRoute ? 'size-full overflow-hidden sm:flex' : 'overflow-y-auto',
                )}
              >
                {isChatRoute && channelsEnabled && (
                  <ThreadList
                    channels={threadListChannels}
                    activeChannelId={activeThreadChannelId}
                    visible={layoutSettings.threadListVisible}
                    showWhenEmpty={channelsEnabled}
                    onImage={hasWorkspaceBackgroundImage}
                    onCreateNew={handleOpenNewChannelModal}
                  />
                )}
                {mainRouteContent}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showWorkspaceManagement ? (
        <ModalPortal
          open={isMembersModalOpen}
          onClose={handleCloseMembers}
          keychainId={KEYCHAIN.MODAL__MEMBERS}
          size="settings"
          className="md:!max-w-2xl"
        >
          <MembersModal open={isMembersModalOpen} onClose={handleCloseMembers} />
        </ModalPortal>
      ) : null}
      {/* Workspace options and invite-people share ONE portal: swapping ?modal= between them
            keeps the portal open, so the backdrop never re-fades (the "black flash"). */}
      {showWorkspaceManagement ? (
        <ModalPortal
          open={isWorkspaceOptionsModalOpen || isInvitePeopleModalOpen}
          onClose={handleCloseWorkspaceOptionsModal}
          keychainId={KEYCHAIN.MODAL__WORKSPACE_OPTIONS}
          size="settings"
          className="md:!h-160 md:!max-h-[calc(100dvh-2rem)] md:!max-w-128"
        >
          {retainedWorkspaceManagementModal === 'invite-people' ? (
            <InvitePeopleModal
              onClose={handleCloseInvitePeople}
              onBack={inviteOpenedFromSettings ? handleShowWorkspaceOptions : undefined}
              initialSelectedUser={onboardingInviteUser}
            />
          ) : retainedWorkspaceManagementModal === 'workspace-options' ? (
            <WorkspaceOptionsModal
              open={isWorkspaceOptionsModalOpen}
              onClose={handleCloseWorkspaceOptionsModal}
              onOpenInvitePeople={handleShowInvitePeople}
              onOpenMembers={handleShowMembersFromWorkspaceOptions}
              onOpenSearch={handleOpenSearchFromSettingsModal}
              defaultSection={workspaceOptionsDefaultSection}
              membersPresentation={WORKSPACE_OPTIONS_MEMBERS_PRESENTATION.SECTION}
            />
          ) : null}
        </ModalPortal>
      ) : null}
      {showDirectMessagePreferences ? (
        <ModalPortal
          open={isDirectMessagePreferencesModalOpen}
          onClose={handleCloseDirectMessagePreferencesModal}
          keychainId={KEYCHAIN.MODAL__DIRECT_MESSAGE_PREFERENCES}
          size="settings"
          className="md:!h-160 md:!max-h-[calc(100dvh-2rem)] md:!max-w-128"
        >
          <DirectMessagePreferencesModal
            open={isDirectMessagePreferencesModalOpen}
            onClose={handleCloseDirectMessagePreferencesModal}
            onOpenSearch={handleOpenSearchFromSettingsModal}
          />
        </ModalPortal>
      ) : null}

      <ModalPortal
        open={isNewChannelModalOpen}
        onClose={handleCloseNewChannelModal}
        keychainId={KEYCHAIN.MODAL__NEW_CHANNEL}
        className="mt-[24vh] max-h-[calc(100dvh-32px)] max-w-[400px] self-start"
      >
        <NewChannelModal onClose={handleCloseNewChannelModal} onChannelCreated={handleChannelCreated} />
      </ModalPortal>

      <ErrorReportModal />

      <ModalPortal
        open={Boolean(channelSettingsModalData)}
        onClose={handleCloseChannelSettingsModal}
        size="settings"
        keychainId={KEYCHAIN.MODAL__CHANNEL_SETTINGS}
        className="pointer-events-none flex items-center justify-center md:!h-160 md:!max-h-[calc(100dvh-2rem)] md:!max-w-128"
      >
        {retainedChannelSettingsModalData ? (
          <ChannelSettings channelId={retainedChannelSettingsModalData.channelId} onClose={handleCloseChannelSettingsModal} />
        ) : null}
      </ModalPortal>

      <ModalPortal
        open={addEmojiModalOpen}
        onClose={handleCloseAddEmojiModal}
        keychainId={KEYCHAIN.MODAL__ADD_EMOJI}
        size="settings"
        className="md:!h-160 md:!max-h-[calc(100dvh-2rem)] md:!max-w-128"
      >
        {addEmojiModalOpen ? (
          <AddEmojiModal onClose={handleCloseAddEmojiModal} onSubmittingChange={handleAddEmojiSubmittingChange} />
        ) : null}
      </ModalPortal>
    </EntitySnapshotContext.Provider>
  );
};

function NoWorkspaceMainContent() {
  const { openNewMessage } = useContext(MainLayoutActionsContext);
  const roundedClasses = useMainPanelRoundedClasses();

  return (
    <div className="relative flex min-h-0 flex-1 max-sm:overflow-hidden" data-app-region="none" data-floating-boundary>
      <div className="relative flex min-w-0 flex-1 flex-col">
        <LayoutNavBar />
        {/* Mobile-only escape hatch back to the chat rows; desktop always shows the rail alongside. */}
        <MobileBackButton onClick={requestSidebarPanel} className="absolute top-4 left-4 z-10 sm:hidden" />
        <div
          className={cn(
            'isolate flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-tl-lg rounded-tr-lg bg-gray-50 p-4',
            roundedClasses[0],
            roundedClasses[1],
          )}
        >
          <div className="flex w-full max-w-[384px] -translate-y-12 flex-col items-center">
            {/* 2x export of the tilted cow, locked to its 244×200 1x size and tucked behind the card. */}
            <img src={StartChatCowImage} alt="" draggable={false} className="h-[200px] w-[244px] shrink-0 select-none" />
            <div className="relative -mt-[13px] flex w-full flex-col items-center gap-7 rounded-[20px] border-[0.5px] border-white bg-gray-100 p-6 shadow-[0px_10px_34px_0px_rgba(0,0,0,0.2)]">
              <div className="flex w-60 max-w-full flex-col gap-4 text-center">
                <h2 className="text-lg leading-6 font-medium tracking-[-0.36px] text-black">Start chatting!</h2>
                <p className="text-[13px] leading-5 tracking-[-0.26px] text-gray-500">
                  Create a group for your community or start a chat with a friend
                </p>
              </div>
              <button
                type="button"
                onClick={openNewMessage}
                data-app-region="none"
                className="flex h-12 w-full cursor-pointer items-center justify-center rounded-full bg-black text-[13px] leading-[18px] font-medium text-white outline-none hover:bg-gray-800 focus-visible:ring-2 focus-visible:ring-gray-600 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-100"
              >
                Start chat
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MainLayoutWorkspaceRouteContent(props) {
  const { workspaceId } = useParams();
  // The persistent rail already owns the destination workspace while the route
  // provider briefly hydrates, so its image can cover that otherwise-gray fallback frame.
  const backgroundSource = findRailItemByKey(props.workspaceRailState.visibleRailItems, `workspace:${workspaceId}`)?.workspace;

  return (
    <RouteWorkspaceProvider
      workspaceId={workspaceId}
      includeEntitySnapshots
      loadingFallback={<MainLayoutContentLoading backgroundSource={backgroundSource} />}
    >
      <MainLayoutContent {...props} />
    </RouteWorkspaceProvider>
  );
}

function MainLayoutDirectMessageRouteContent(props) {
  const { directMessageId } = useParams();
  const backgroundSource = findRailItemByKey(props.workspaceRailState.visibleRailItems, `direct_message:${directMessageId}`)?.directMessage;

  return (
    <DirectMessageRouteProvider
      directMessageId={directMessageId}
      includeEntitySnapshots
      loadingFallback={<MainLayoutContentLoading backgroundSource={backgroundSource} />}
    >
      <MainLayoutContent {...props} />
    </DirectMessageRouteProvider>
  );
}

function MainLayoutRoutedFrame(props) {
  const { workspaceRailState } = props;
  const [location] = useLocation();
  const activeDirectMessageId = routeSegmentFromPath(location, '/messages');
  const workspaceRouteId = routeSegmentFromPath(location, '/spaces');
  const directMessageRailItem = activeDirectMessageId
    ? findRailItemByKey(workspaceRailState.visibleRailItems, `direct_message:${activeDirectMessageId}`)
    : null;
  const directMessage = useLiveQuery(
    () => (activeDirectMessageId ? db.direct_messages.get(String(activeDirectMessageId)) : null),
    [activeDirectMessageId],
    null,
  );
  const queriedDirectMessageWorkspaceId =
    directMessage?.id && String(directMessage.id) === activeDirectMessageId && directMessage.workspace_id
      ? String(directMessage.workspace_id)
      : null;
  const directMessageWorkspaceId = directMessageRailItem ? railItemWorkspaceId(directMessageRailItem) : queriedDirectMessageWorkspaceId;
  const workspaceId = workspaceRouteId || directMessageWorkspaceId;
  const { userEmail, profile, user } = useCurrentUserShell(workspaceId);

  useEffect(
    function rememberRoutedWorkspaceChannel() {
      rememberWorkspaceChannelRoute(location);
    },
    [location],
  );

  if (!isMainLayoutRoute(location)) return null;

  return (
    <MainLayoutFrame
      workspaceId={workspaceId}
      activeDirectMessageId={activeDirectMessageId}
      workspaceRailState={workspaceRailState}
      userEmail={userEmail}
      currentUser={user}
      currentProfile={profile}
    >
      {/* A sibling of the routes, not a descendant: the route providers swap their whole
          subtree out for a loading fallback while they hydrate, and that used to take the
          call's video elements with it on every navigation. */}
      <ConferenceCallLayer />
      <Route path="/chat">
        <NoWorkspaceMainContent />
      </Route>
      <Route path="/messages/:directMessageId" nest>
        <MainLayoutDirectMessageRouteContent {...props} />
      </Route>
      <Route path="/spaces/:workspaceId" nest>
        <MainLayoutWorkspaceRouteContent {...props} />
      </Route>
    </MainLayoutFrame>
  );
}

export default function MainLayoutWithRouter(props) {
  const workspaceRailState = useAuthenticatedShellRailState();

  return (
    <Router>
      {/* Above MainLayoutRoutedFrame so the LiveKit room outlives route changes —
          switching channels, workspaces, or DMs must never end an active call. */}
      <ConferenceProvider>
        <MainLayoutRoutedFrame {...props} workspaceRailState={workspaceRailState} />
      </ConferenceProvider>
    </Router>
  );
}
